import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import TwoFactorModal from '../../components/admin/TwoFactorModal';
import {
  Upload,
  Plus,
  Search,
  Filter,
  FileText,
  FileType,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  ChevronRight,
  AlertTriangle,
  Archive,
  Send,
  File,
  X,
  Copy,
  Tag,
  ArrowRight,
  BookOpen,
  FileEdit,
  Terminal,
  ClipboardList,
  RotateCcw,
  History,
  User,
  Calendar,
  Link,
  Shield,
  Sparkles,
  Zap,
  Target,
  Info,
  ChevronDown,
  ChevronUp,
  Layers,
  Lock,
  Settings2,
  Settings,
  Lightbulb,
  Play,
  Check,
  AlertCircle,
  Loader2,
  ThumbsUp,
  ThumbsDown,
  GitBranch,
  Library,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

// Status configuration
const STATUS_CONFIG = {
  uploaded: { label: 'Uploaded', color: 'bg-slate-500/10 text-slate-400 border-slate-500/30', icon: Upload },
  draft: { label: 'Draft', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: Clock },
  needs_review: { label: 'Needs Review', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: Eye },
  approved: { label: 'Approved', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  live: { label: 'Live', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: Send },
  archived: { label: 'Archived', color: 'bg-slate-600/10 text-slate-500 border-slate-600/30', icon: Archive },
  rejected: { label: 'Rejected', color: 'bg-red-500/10 text-red-400 border-red-500/30', icon: XCircle },
};

// Content type options
const CONTENT_TYPES = [
  { value: 'transcript', label: 'Transcript' },
  { value: 'sop', label: 'SOP' },
  { value: 'metro2_instruction', label: 'Metro 2 Instruction' },
  { value: 'dispute_example', label: 'Dispute Example' },
  { value: 'provider_guide', label: 'Provider Guide' },
  { value: 'prompt_reference', label: 'Prompt Reference' },
  { value: 'template_reference', label: 'Template Reference' },
  { value: 'compliance_note', label: 'Compliance Note' },
  { value: 'general_knowledge', label: 'General Knowledge' },
  { value: 'letter_example', label: 'Letter Example' },
];

// Destination options with behavior clarification
// IMPORTANT: Promotion creates INDEPENDENT COPIES in destination systems
// The promoted records do NOT remain synced with the source intake item
const DESTINATIONS = [
  { value: 'knowledge_base', label: 'AI Knowledge Base', icon: BookOpen, color: 'text-emerald-400', description: 'Creates a knowledge entry for AI training' },
  { value: 'letter_training', label: 'Letter Training', icon: FileEdit, color: 'text-emerald-400', description: 'Creates a letter example for dispute training' },
  { value: 'prompt_support', label: 'Prompt Management', icon: Terminal, color: 'text-amber-400', description: 'Creates a prompt reference document' },
  { value: 'extraction_rules', label: 'Extraction Rules', icon: Filter, color: 'text-teal-400', description: 'Signal detection and normalization rules' },
  { value: 'routing_rules', label: 'Routing Rules', icon: GitBranch, color: 'text-orange-400', description: 'Classification and method routing logic' },
  { value: 'method_library', label: 'Method Library', icon: Library, color: 'text-emerald-400', description: 'Strategy method cards and definitions' },
  { value: 'template_manager', label: 'Template Manager', icon: FileType, color: 'text-teal-400', description: 'Creates a template for letter generation' },
  { value: 'review_queue', label: 'Review Queue', icon: ClipboardList, color: 'text-amber-400', description: 'Creates a flagged item for manual review' },
];

// AI Assist modes
const AI_ASSIST_MODES = [
  { value: 'off', label: 'No AI Assist', description: 'Save as-is without AI processing' },
  { value: 'classify_tags', label: 'Classification + Tags', description: 'Auto-classify content type and suggest tags' },
  { value: 'full', label: 'Full AI Assist', description: 'Summary, tags, routing suggestions, and key points' },
  { value: 'deep', label: 'Deep Analysis', description: 'Comprehensive extraction with section breakdown, noise detection, and multi-destination routing' },
];

// AI Assist tasks
const AI_ASSIST_TASKS = [
  { id: 'classify', label: 'Classify content type', description: 'Detect if SOP, transcript, dispute example, etc.' },
  { id: 'tags', label: 'Suggest tags', description: 'Generate relevant tags based on content' },
  { id: 'destinations', label: 'Suggest destinations', description: 'Recommend where this content should go' },
  { id: 'summary', label: 'Generate summary', description: 'Create a brief summary of the content' },
  { id: 'keypoints', label: 'Extract key training points', description: 'Identify important learning points' },
];

// Deep analysis progress stages
const DEEP_ANALYSIS_STAGES = [
  { id: 'analyzing', label: 'Analyzing content...', description: 'Reading and understanding the upload' },
  { id: 'extracting', label: 'Extracting training sections...', description: 'Breaking content into meaningful parts' },
  { id: 'detecting', label: 'Detecting noise and irrelevant content...', description: 'Flagging speaker names, marketing, filler' },
  { id: 'routing', label: 'Preparing destination routing...', description: 'Determining where each section should go' },
  { id: 'creating', label: 'Creating downstream elements...', description: 'Building linked entries in target modules' },
];

// Section type colors and labels
const SECTION_TYPE_CONFIG = {
  doctrine: { label: 'Doctrine', color: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
  strategy: { label: 'Strategy', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  workflow: { label: 'Workflow', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  dispute_rule: { label: 'Dispute Rule', color: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
  method_trigger: { label: 'Method Trigger', color: 'text-teal-400 bg-teal-500/10 border-teal-500/30' },
  example: { label: 'Example', color: 'text-pink-400 bg-pink-500/10 border-pink-500/30' },
  template: { label: 'Template', color: 'text-teal-400 bg-teal-500/10 border-teal-500/30' },
  prompt_support: { label: 'Prompt Support', color: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
  reference: { label: 'Reference', color: 'text-slate-400 bg-slate-500/10 border-slate-500/30' },
  noise: { label: 'Noise', color: 'text-red-400 bg-red-500/10 border-red-500/30' },
  unknown: { label: 'Unknown', color: 'text-slate-500 bg-slate-600/10 border-slate-600/30' },
};

// Source type labels
const SOURCE_TYPE_LABELS = {
  pasted_text: 'Pasted Text',
  file_upload: 'File Upload',
  manual_entry: 'Manual Entry',
  api_import: 'API Import',
  duplicate: 'Duplicated',
};

// Format timestamps
const formatUTCTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toISOString().replace('T', ' ').replace('Z', ' UTC');
};

const formatLocalTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleString();
};

// AI Suggestion Card Component
const AISuggestionCard = ({ 
  title, 
  icon: Icon, 
  suggestion, 
  currentValue, 
  onAccept, 
  onReject,
  accepted,
  rejected,
  renderValue,
  confidence 
}) => {
  const isAccepted = accepted === true;
  const isRejected = rejected === true;
  const isPending = !isAccepted && !isRejected;
  
  return (
    <div className={`rounded-lg border p-3 transition-all ${
      isAccepted ? 'bg-emerald-500/10 border-emerald-500/30' :
      isRejected ? 'bg-stone-800/50 border-slate-700 opacity-60' :
      'bg-amber-500/10 border-amber-500/30'
    }`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Icon size={14} className={isAccepted ? 'text-emerald-400' : isRejected ? 'text-slate-500' : 'text-amber-400'} />
          <span className="text-sm font-medium text-white">{title}</span>
        </div>
        {confidence && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${
            confidence > 0.8 ? 'bg-emerald-500/20 text-emerald-400' :
            confidence > 0.5 ? 'bg-amber-500/20 text-amber-400' :
            'bg-slate-500/20 text-slate-400'
          }`}>
            {Math.round(confidence * 100)}%
          </span>
        )}
      </div>
      
      <div className="mb-2">
        {renderValue ? renderValue(suggestion) : (
          <p className="text-sm text-slate-300">{suggestion}</p>
        )}
      </div>
      
      {currentValue && currentValue !== suggestion && !isAccepted && !isRejected && (
        <p className="text-xs text-slate-500 mb-2">
          Current: <span className="text-slate-400">{renderValue ? 'different value' : currentValue}</span>
        </p>
      )}
      
      {isPending && (
        <div className="flex gap-2 mt-2">
          <Button size="sm" variant="ghost" onClick={onAccept} className="text-emerald-400 hover:bg-emerald-500/20 h-7 px-2">
            <Check size={12} className="mr-1" /> Accept
          </Button>
          <Button size="sm" variant="ghost" onClick={onReject} className="text-slate-400 hover:bg-slate-700 h-7 px-2">
            <X size={12} className="mr-1" /> Ignore
          </Button>
        </div>
      )}
      
      {isAccepted && (
        <div className="flex items-center gap-1 text-xs text-emerald-400 mt-1">
          <CheckCircle size={12} /> Accepted
        </div>
      )}
      
      {isRejected && (
        <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
          <XCircle size={12} /> Ignored
        </div>
      )}
    </div>
  );
};

// Processing Impact Preview Component
const ProcessingImpactPreview = ({ aiAssistMode, aiTasks, destination, contentType, acceptedSuggestions }) => {
  const dest = DESTINATIONS.find(d => d.value === destination);
  const DestIcon = dest?.icon || FileText;
  const contentTypeLabel = CONTENT_TYPES.find(c => c.value === contentType)?.label || contentType;
  
  const getCreationImpact = () => {
    const items = ['1 master intake item will be created'];
    if (destination === 'knowledge_base') {
      items.push('1 linked knowledge entry will be created (after approval)');
    } else if (destination === 'letter_training') {
      items.push('1 linked letter training example will be created (after approval)');
    } else if (destination === 'prompt_support') {
      items.push('1 linked prompt reference will be created (after approval)');
    } else if (destination === 'template_manager') {
      items.push('1 linked template reference will be created (after approval)');
    } else if (destination === 'review_queue') {
      items.push('1 review queue item will be created (after approval)');
    }
    return items;
  };
  
  const getAIActions = () => {
    if (aiAssistMode === 'off') return [];
    
    const actions = [];
    if (aiAssistMode === 'classify_tags' || aiAssistMode === 'full') {
      if (aiTasks.classify) actions.push('suggest content type classification');
      if (aiTasks.tags) actions.push('suggest relevant tags');
    }
    if (aiAssistMode === 'full') {
      if (aiTasks.destinations) actions.push('suggest optimal destinations');
      if (aiTasks.summary) actions.push('generate content summary');
      if (aiTasks.keypoints) actions.push('extract key training points');
    }
    return actions;
  };
  
  const getSafetyGuarantees = () => [
    'will not make content live automatically',
    'will not overwrite existing prompts',
    'will not change production methods',
    'will not publish templates',
    'will not skip admin review',
  ];
  
  return (
    <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4 space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium text-white">
        <Target size={16} className="text-amber-400" />
        Processing Impact
      </div>
      
      {/* What will be created */}
      <div className="space-y-1">
        <p className="text-xs text-slate-400 uppercase tracking-wide">What will be created</p>
        <ul className="space-y-1">
          {getCreationImpact().map((item, idx) => (
            <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
              <Layers size={12} className="text-emerald-400 mt-1 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
      
      {/* Content Type */}
      <div className="space-y-1">
        <p className="text-xs text-slate-400 uppercase tracking-wide">Content Type</p>
        <p className="text-sm text-white">{contentTypeLabel}</p>
      </div>
      
      {/* Where it will go */}
      <div className="space-y-1">
        <p className="text-xs text-slate-400 uppercase tracking-wide">Destination</p>
        <div className="flex items-center gap-2 text-sm text-slate-300">
          <DestIcon size={14} className={dest?.color || 'text-slate-400'} />
          {dest?.label || destination}
        </div>
      </div>
      
      {/* Accepted AI Suggestions indicator */}
      {acceptedSuggestions && Object.values(acceptedSuggestions).some(v => v) && (
        <div className="space-y-1 bg-emerald-500/10 rounded-lg p-2">
          <p className="text-xs text-emerald-400 uppercase tracking-wide flex items-center gap-1">
            <Sparkles size={10} /> AI Suggestions Applied
          </p>
          <ul className="space-y-0.5">
            {acceptedSuggestions.classify && <li className="text-xs text-emerald-300">Content type from AI</li>}
            {acceptedSuggestions.destinations && <li className="text-xs text-emerald-300">Destination from AI</li>}
            {acceptedSuggestions.tags && <li className="text-xs text-emerald-300">Tags from AI</li>}
            {acceptedSuggestions.summary && <li className="text-xs text-emerald-300">Summary from AI</li>}
            {acceptedSuggestions.keypoints && <li className="text-xs text-emerald-300">Key points from AI</li>}
          </ul>
        </div>
      )}
      
      {/* AI Assist actions */}
      {getAIActions().length > 0 && aiAssistMode !== 'off' && (
        <div className="space-y-1">
          <p className="text-xs text-slate-400 uppercase tracking-wide">AI Assist will</p>
          <ul className="space-y-1">
            {getAIActions().map((action, idx) => (
              <li key={idx} className="text-sm text-amber-400 flex items-start gap-2">
                <Sparkles size={12} className="mt-1 shrink-0" />
                {action}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {aiAssistMode === 'off' && (
        <div className="text-sm text-slate-400 italic flex items-center gap-2">
          <Info size={14} />
          AI Assist is disabled. Content will be saved as-is.
        </div>
      )}
      
      {/* Safety guarantees */}
      <div className="space-y-1 pt-2 border-t border-slate-700">
        <p className="text-xs text-slate-400 uppercase tracking-wide flex items-center gap-1">
          <Lock size={10} />
          What will NOT happen automatically
        </p>
        <ul className="space-y-1">
          {getSafetyGuarantees().map((item, idx) => (
            <li key={idx} className="text-xs text-slate-500 flex items-start gap-2">
              <Shield size={10} className="text-slate-600 mt-0.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
      
      {/* Draft notice */}
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 mt-2">
        <p className="text-xs text-emerald-400 flex items-start gap-2">
          <CheckCircle size={12} className="mt-0.5 shrink-0" />
          All outputs will remain in Draft until reviewed by an admin. Raw content remains stored internally.
        </p>
      </div>
    </div>
  );
};

// AI Assist Panel Component
const AIAssistPanel = ({
  aiAssistMode,
  setAiAssistMode,
  aiTasks,
  setAiTasks,
  content,
  title,
  currentContentType,
  currentDestination,
  currentTags,
  onSuggestionsReady,
  suggestions,
  setSuggestions,
  acceptedSuggestions,
  setAcceptedSuggestions,
  isProcessing,
  setIsProcessing,
  processingError,
  setProcessingError,
  onApplySuggestion,
}) => {
  const { getAuthHeaders } = useAuth();
  
  // Update tasks based on mode
  useEffect(() => {
    if (aiAssistMode === 'off') {
      setAiTasks({ classify: false, tags: false, destinations: false, summary: false, keypoints: false });
    } else if (aiAssistMode === 'classify_tags') {
      setAiTasks({ classify: true, tags: true, destinations: false, summary: false, keypoints: false });
    } else if (aiAssistMode === 'full') {
      setAiTasks({ classify: true, tags: true, destinations: true, summary: true, keypoints: true });
    }
  }, [aiAssistMode, setAiTasks]);
  
  const handleRunAIAssist = async () => {
    if (!content || content.trim().length < 20) {
      toast.error('Please enter at least 20 characters of content before running AI Assist');
      return;
    }
    
    setIsProcessing(true);
    setProcessingError(null);
    setSuggestions(null);
    setAcceptedSuggestions({});
    
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const result = await adminOpsService.runAIAssist(
        content,
        title,
        currentContentType,
        currentDestination,
        aiTasks
      );
      
      if (result.success && result.suggestions) {
        setSuggestions(result.suggestions);
        onSuggestionsReady?.(result.suggestions);
        toast.success('AI Assist completed! Review suggestions below.');
      } else {
        throw new Error('Invalid response from AI');
      }
    } catch (error) {
      console.error('AI Assist error:', error);
      setProcessingError(error.message || 'AI Assist failed. Please try again.');
      toast.error(error.message || 'AI Assist failed');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleAcceptSuggestion = (type) => {
    setAcceptedSuggestions(prev => ({ ...prev, [type]: true }));
    
    // Apply the suggestion immediately
    if (suggestions) {
      if (type === 'classify' && suggestions.content_type) {
        onApplySuggestion('content_type', suggestions.content_type.suggested);
      } else if (type === 'destinations' && suggestions.destinations) {
        onApplySuggestion('destination', suggestions.destinations.primary);
      } else if (type === 'tags' && suggestions.tags) {
        onApplySuggestion('tags', suggestions.tags.suggested);
      } else if (type === 'summary' && suggestions.summary) {
        onApplySuggestion('summary', suggestions.summary.text);
      } else if (type === 'keypoints' && suggestions.key_points) {
        onApplySuggestion('keypoints', suggestions.key_points.points);
      }
    }
  };
  
  const handleRejectSuggestion = (type) => {
    setAcceptedSuggestions(prev => ({ ...prev, [type]: false }));
  };
  
  const handleAcceptAll = () => {
    if (!suggestions) return;
    
    const allAccepted = {};
    if (suggestions.content_type) {
      allAccepted.classify = true;
      onApplySuggestion('content_type', suggestions.content_type.suggested);
    }
    if (suggestions.destinations) {
      allAccepted.destinations = true;
      onApplySuggestion('destination', suggestions.destinations.primary);
    }
    if (suggestions.tags) {
      allAccepted.tags = true;
      onApplySuggestion('tags', suggestions.tags.suggested);
    }
    if (suggestions.summary) {
      allAccepted.summary = true;
      onApplySuggestion('summary', suggestions.summary.text);
    }
    if (suggestions.key_points) {
      allAccepted.keypoints = true;
      onApplySuggestion('keypoints', suggestions.key_points.points);
    }
    
    setAcceptedSuggestions(allAccepted);
    toast.success('All AI suggestions applied!');
  };
  
  return (
    <div className="space-y-4">
      {/* AI Assist Header */}
      <div className="bg-gradient-to-br from-amber-500/10 to-emerald-500/10 border border-amber-500/20 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Sparkles size={20} className="text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">AI Assist</p>
              <p className="text-xs text-slate-400">
                {aiAssistMode === 'off' ? 'Disabled' : 
                 aiAssistMode === 'classify_tags' ? 'Classification + Tags' : 
                 aiAssistMode === 'deep' ? 'Deep Analysis (comprehensive)' :
                 'Full AI Assist'}
              </p>
            </div>
          </div>
          <Select value={aiAssistMode} onValueChange={setAiAssistMode}>
            <SelectTrigger className="w-48 bg-stone-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-slate-700">
              {AI_ASSIST_MODES.map(mode => (
                <SelectItem key={mode.value} value={mode.value}>
                  {mode.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Run button */}
        {aiAssistMode !== 'off' && !suggestions && (
          <Button
            onClick={handleRunAIAssist}
            disabled={isProcessing || !content || content.length < 20}
            className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
            data-testid="run-ai-assist-btn"
          >
            {isProcessing ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Analyzing content...
              </>
            ) : (
              <>
                <Play size={16} className="mr-2" />
                Run AI Assist
              </>
            )}
          </Button>
        )}
        
        {/* Processing states */}
        {isProcessing && (
          <div className="mt-4 p-4 bg-stone-800/50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <Loader2 size={20} className="text-amber-400 animate-spin" />
              <div>
                <p className="text-sm font-medium text-white">Processing with AI</p>
                <p className="text-xs text-slate-400">Analyzing content and generating suggestions...</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></div>
                Classifying content type...
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                Generating tags...
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                Determining best destinations...
              </div>
            </div>
          </div>
        )}
        
        {/* Error state */}
        {processingError && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-400">AI Assist Failed</p>
                <p className="text-xs text-slate-400 mt-1">{processingError}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleRunAIAssist}
                  className="mt-2 text-red-400 hover:bg-red-500/20"
                >
                  <RefreshCw size={14} className="mr-1" />
                  Retry
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Privacy note */}
        {aiAssistMode !== 'off' && !suggestions && !isProcessing && (
          <div className="mt-3 p-2 bg-stone-800/50 rounded-lg">
            <p className="text-xs text-slate-500 flex items-start gap-1">
              <Shield size={10} className="mt-0.5 shrink-0" />
              Only sanitized content is sent for AI processing. Raw data stays internal.
            </p>
          </div>
        )}
      </div>
      
      {/* Suggestions Panel */}
      {suggestions && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-white flex items-center gap-2">
              <Lightbulb size={14} className="text-amber-400" />
              AI Suggestions
            </h4>
            <Button size="sm" variant="ghost" onClick={handleAcceptAll} className="text-emerald-400 hover:bg-emerald-500/20">
              <Check size={14} className="mr-1" />
              Accept All
            </Button>
          </div>
          
          <div className="grid gap-3">
            {/* Content Type Suggestion */}
            {suggestions.content_type && (
              <AISuggestionCard
                title="Content Type"
                icon={FileType}
                suggestion={suggestions.content_type.suggested}
                currentValue={currentContentType}
                confidence={suggestions.content_type.confidence}
                accepted={acceptedSuggestions.classify}
                rejected={acceptedSuggestions.classify === false}
                onAccept={() => handleAcceptSuggestion('classify')}
                onReject={() => handleRejectSuggestion('classify')}
                renderValue={(val) => (
                  <div>
                    <p className="text-sm text-white font-medium">
                      {CONTENT_TYPES.find(c => c.value === val)?.label || val}
                    </p>
                    {suggestions.content_type.reason && (
                      <p className="text-xs text-slate-400 mt-1">{suggestions.content_type.reason}</p>
                    )}
                  </div>
                )}
              />
            )}
            
            {/* Destination Suggestion */}
            {suggestions.destinations && (
              <AISuggestionCard
                title="Destination"
                icon={ArrowRight}
                suggestion={suggestions.destinations.primary}
                currentValue={currentDestination}
                confidence={suggestions.destinations.confidence}
                accepted={acceptedSuggestions.destinations}
                rejected={acceptedSuggestions.destinations === false}
                onAccept={() => handleAcceptSuggestion('destinations')}
                onReject={() => handleRejectSuggestion('destinations')}
                renderValue={(val) => {
                  const dest = DESTINATIONS.find(d => d.value === val);
                  const DestIcon = dest?.icon || FileText;
                  return (
                    <div>
                      <div className="flex items-center gap-2">
                        <DestIcon size={14} className={dest?.color || 'text-slate-400'} />
                        <span className="text-sm text-white font-medium">{dest?.label || val}</span>
                      </div>
                      {suggestions.destinations.reason && (
                        <p className="text-xs text-slate-400 mt-1">{suggestions.destinations.reason}</p>
                      )}
                    </div>
                  );
                }}
              />
            )}
            
            {/* Tags Suggestion */}
            {suggestions.tags && suggestions.tags.suggested?.length > 0 && (
              <AISuggestionCard
                title="Tags"
                icon={Tag}
                suggestion={suggestions.tags.suggested}
                currentValue={currentTags?.join(', ')}
                confidence={suggestions.tags.confidence}
                accepted={acceptedSuggestions.tags}
                rejected={acceptedSuggestions.tags === false}
                onAccept={() => handleAcceptSuggestion('tags')}
                onReject={() => handleRejectSuggestion('tags')}
                renderValue={(tags) => (
                  <div className="flex flex-wrap gap-1">
                    {tags.map(tag => (
                      <span key={tag} className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-300 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              />
            )}
            
            {/* Summary Suggestion */}
            {suggestions.summary && suggestions.summary.text && (
              <AISuggestionCard
                title="Summary"
                icon={FileText}
                suggestion={suggestions.summary.text}
                confidence={suggestions.summary.confidence}
                accepted={acceptedSuggestions.summary}
                rejected={acceptedSuggestions.summary === false}
                onAccept={() => handleAcceptSuggestion('summary')}
                onReject={() => handleRejectSuggestion('summary')}
                renderValue={(text) => (
                  <p className="text-sm text-slate-300">{text}</p>
                )}
              />
            )}
            
            {/* Key Points Suggestion */}
            {suggestions.key_points && suggestions.key_points.points?.length > 0 && (
              <AISuggestionCard
                title="Key Training Points"
                icon={Lightbulb}
                suggestion={suggestions.key_points.points}
                confidence={suggestions.key_points.confidence}
                accepted={acceptedSuggestions.keypoints}
                rejected={acceptedSuggestions.keypoints === false}
                onAccept={() => handleAcceptSuggestion('keypoints')}
                onReject={() => handleRejectSuggestion('keypoints')}
                renderValue={(points) => (
                  <ul className="space-y-1">
                    {points.map((point, idx) => (
                      <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                        <span className="text-amber-400">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                )}
              />
            )}
          </div>
          
          {/* Re-run button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSuggestions(null);
              setAcceptedSuggestions({});
            }}
            className="w-full mt-2"
          >
            <RefreshCw size={14} className="mr-2" />
            Clear & Run Again
          </Button>
        </div>
      )}
    </div>
  );
};

// Deep Analysis Panel Component
const DeepAnalysisPanel = ({
  content,
  title,
  deepAnalysisData,
  setDeepAnalysisData,
  isProcessing,
  setIsProcessing,
  processingStage,
  setProcessingStage,
  sectionSelections,
  setSectionSelections,
  onApplyDeepAnalysis,
  onSwitchToStandard, // Callback to switch to standard AI assist mode
}) => {
  const { getAuthHeaders } = useAuth();
  const [showAllSections, setShowAllSections] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [viewingSection, setViewingSection] = useState(null);  // For full section view modal
  
  const handleRunDeepAnalysis = async (isRetry = false) => {
    if (!content || content.trim().length < 50) {
      toast.error('Please enter at least 50 characters of content for deep analysis');
      return;
    }
    
    setIsProcessing(true);
    setProcessingStage('analyzing');
    setAnalysisError(null);
    
    // Don't clear deepAnalysisData on retry - we might show partial results
    if (!isRetry) {
      setDeepAnalysisData(null);
    }
    
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      
      // Progress callback for async job polling
      const handleProgress = (stage, progress) => {
        // Map backend stages to user-friendly stages
        const stageMap = {
          'initializing': 'Starting analysis...',
          'analyzing': 'Extracting sections...',
          'extracting': 'Detecting noise...',
          'detecting': 'Classifying modules...',
          'routing': 'Building response...',
          'complete': 'Finalizing...'
        };
        setProcessingStage(stageMap[stage] || stage);
      };
      
      // Set initial stage
      setProcessingStage('Starting analysis...');
      
      // Use the polling-based method for reliable long-content handling
      const result = await adminOpsService.runDeepAnalysisWithPolling(content, title, handleProgress);
      
      setProcessingStage(null);
      
      // Check if the analysis succeeded
      if (result.success === false) {
        // Analysis failed - show error state with retry/fallback options
        setAnalysisError({
          message: result.error_message || 'Deep analysis failed',
          type: result.error_type || 'unknown',
          retryAvailable: result.retry_available !== false,
          fallbackAvailable: result.fallback_available !== false,
          partialResult: result.partial_result || false
        });
        
        // If there's a partial result, still show what we got
        if (result.sections && result.sections.length > 0) {
          setDeepAnalysisData({ ...result, partial: true });
          const selections = {};
          result.sections.forEach(section => {
            selections[section.section_id] = {
              selected: !section.is_noise && section.recommended_action !== 'reject',
              action: section.recommended_action || 'review',
              destination: section.recommended_destination || 'knowledge_base'
            };
          });
          setSectionSelections(selections);
        }
        
        if (isRetry) {
          setRetryCount(prev => prev + 1);
        }
        
        toast.error(result.error_message || 'Deep analysis failed - see options below');
      } else {
        // Success!
        setDeepAnalysisData(result);
        setAnalysisError(null);
        setRetryCount(0);
        
        // Initialize section selections
        const selections = {};
        (result.sections || []).forEach(section => {
          selections[section.section_id] = {
            selected: !section.is_noise && section.recommended_action !== 'reject',
            action: section.recommended_action || 'review',
            destination: section.recommended_destination || 'knowledge_base'
          };
        });
        setSectionSelections(selections);
        
        toast.success(`Deep analysis complete: ${result.useful_sections || 0} useful sections, ${result.noise_sections || 0} noise flagged`);
      }
    } catch (error) {
      console.error('Deep analysis error:', error);
      
      setProcessingStage(null);
      
      // Determine error type for better messaging
      const errorMessage = error.message || 'Deep analysis failed unexpectedly';
      const isTimeout = errorMessage.toLowerCase().includes('timeout') || error.name === 'TimeoutError';
      const is502 = errorMessage.includes('502');
      
      // Set error state with retry/fallback options
      setAnalysisError({
        message: is502 
          ? 'Server timeout during analysis. Your content is preserved - please retry.' 
          : isTimeout
            ? 'Analysis timed out. The content may be very complex. Please retry or try standard analysis.'
            : errorMessage,
        type: is502 ? 'gateway_timeout' : isTimeout ? 'timeout' : 'api_error',
        retryAvailable: true,
        fallbackAvailable: true,
        partialResult: false
      });
      
      if (isRetry) {
        setRetryCount(prev => prev + 1);
      }
      
      toast.error(is502 
        ? 'Server timeout - your content is preserved. Try again.' 
        : (errorMessage || 'Deep analysis failed'));
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Cleanup on unmount - no longer needed since we don't use intervals
  React.useEffect(() => {
    return () => {
      // Cleanup if needed
    };
  }, []);
  
  const handleRetry = () => {
    handleRunDeepAnalysis(true);
  };
  
  const handleSwitchToStandard = () => {
    setAnalysisError(null);
    setDeepAnalysisData(null);
    setSectionSelections({});
    if (onSwitchToStandard) {
      onSwitchToStandard();
    }
    toast.info('Switched to standard AI assist mode');
  };
  
  const handleSaveManually = () => {
    setAnalysisError(null);
    // Keep any partial results if available
    toast.info('You can now save manually without AI analysis');
  };
  
  const toggleSectionSelection = (sectionId, field, value) => {
    setSectionSelections(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [field]: value
      }
    }));
  };
  
  const getSelectedSectionsCount = () => {
    return Object.values(sectionSelections).filter(s => s.selected).length;
  };
  
  const currentStageInfo = processingStage ? DEEP_ANALYSIS_STAGES.find(s => s.id === processingStage) : null;
  
  // Get error type display info
  const getErrorInfo = (type) => {
    const errorTypes = {
      timeout: {
        icon: Clock,
        color: 'text-amber-400',
        title: 'Analysis Timed Out',
        suggestion: 'The content may be too long. Try breaking it into smaller pieces or use standard analysis.'
      },
      parse_error: {
        icon: AlertCircle,
        color: 'text-red-400',
        title: 'Response Parsing Failed',
        suggestion: 'The AI response was incomplete. Retrying usually fixes this.'
      },
      api_error: {
        icon: AlertCircle,
        color: 'text-red-400',
        title: 'API Error',
        suggestion: 'There was a problem communicating with the AI service.'
      },
      content_too_long: {
        icon: FileText,
        color: 'text-amber-400',
        title: 'Content Too Long',
        suggestion: 'Consider splitting this content into multiple intake items.'
      }
    };
    return errorTypes[type] || errorTypes.api_error;
  };
  
  return (
    <div className="space-y-4">
      {/* Error State with Retry/Fallback Options */}
      {analysisError && !isProcessing && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            {(() => {
              const errorInfo = getErrorInfo(analysisError.type);
              const ErrorIcon = errorInfo.icon;
              return (
                <>
                  <div className={`w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0`}>
                    <ErrorIcon className={errorInfo.color} size={20} />
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-medium ${errorInfo.color}`}>{errorInfo.title}</h4>
                    <p className="text-sm text-slate-400 mt-1">{analysisError.message}</p>
                    <p className="text-xs text-slate-500 mt-2">{errorInfo.suggestion}</p>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {analysisError.retryAvailable && retryCount < 3 && (
                        <Button
                          size="sm"
                          onClick={handleRetry}
                          className="bg-amber-600 hover:bg-amber-700"
                          data-testid="retry-deep-analysis-btn"
                        >
                          <RefreshCw size={14} className="mr-1" />
                          Retry Deep Analysis
                          {retryCount > 0 && <span className="ml-1 text-xs opacity-70">({retryCount}/3)</span>}
                        </Button>
                      )}
                      
                      {analysisError.fallbackAvailable && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleSwitchToStandard}
                          className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                        >
                          <Zap size={14} className="mr-1" />
                          Use Standard Analysis
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleSaveManually}
                        className="text-slate-400 hover:text-white"
                      >
                        <FileText size={14} className="mr-1" />
                        Save Without AI
                      </Button>
                    </div>
                    
                    {/* Preserved Content Notice */}
                    <div className="mt-4 p-2 bg-stone-800/50 rounded-lg flex items-start gap-2">
                      <Shield size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-slate-400">
                        Your content and form values are preserved. Nothing has been lost.
                      </p>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      
      {/* Run Analysis Button */}
      {!deepAnalysisData && !isProcessing && !analysisError && (
        <div className="bg-gradient-to-r from-amber-500/10 to-emerald-500/10 border border-amber-500/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Zap className="text-amber-400" size={20} />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-white">Deep Analysis Mode</h4>
              <p className="text-sm text-slate-400 mt-1">
                Comprehensive extraction with section breakdown, noise detection, and multi-destination routing.
                Takes longer but provides richer, more structured output.
              </p>
              <Button
                onClick={handleRunDeepAnalysis}
                className="mt-3 bg-amber-600 hover:bg-amber-700"
                disabled={!content || content.length < 50}
                data-testid="run-deep-analysis-btn"
              >
                <Sparkles size={14} className="mr-2" />
                Run Deep Analysis
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Processing State */}
      {isProcessing && (
        <div className="bg-stone-800/50 border border-amber-500/30 rounded-xl p-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <Loader2 size={40} className="animate-spin text-amber-400" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles size={16} className="text-amber-300" />
              </div>
            </div>
            
            {currentStageInfo && (
              <>
                <p className="text-white font-medium">{currentStageInfo.label}</p>
                <p className="text-sm text-slate-400">{currentStageInfo.description}</p>
              </>
            )}
            
            {/* Stage Progress */}
            <div className="w-full max-w-md space-y-2">
              {DEEP_ANALYSIS_STAGES.slice(0, 4).map((stage, idx) => {
                const currentIdx = DEEP_ANALYSIS_STAGES.findIndex(s => s.id === processingStage);
                const isComplete = idx < currentIdx;
                const isCurrent = idx === currentIdx;
                
                return (
                  <div key={stage.id} className="flex items-center gap-2">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      isComplete ? 'bg-emerald-500' : isCurrent ? 'bg-amber-500 animate-pulse' : 'bg-slate-700'
                    }`}>
                      {isComplete ? (
                        <CheckCircle size={12} className="text-white" />
                      ) : isCurrent ? (
                        <Loader2 size={12} className="text-white animate-spin" />
                      ) : (
                        <span className="w-2 h-2 rounded-full bg-slate-500" />
                      )}
                    </div>
                    <span className={`text-sm ${isCurrent ? 'text-white' : isComplete ? 'text-emerald-400' : 'text-slate-500'}`}>
                      {stage.label.replace('...', '')}
                    </span>
                  </div>
                );
              })}
            </div>
            
            <p className="text-xs text-slate-500">This may take 10-30 seconds for thorough analysis...</p>
          </div>
        </div>
      )}
      
      {/* Analysis Results */}
      {deepAnalysisData && !isProcessing && (
        <div className="space-y-4">
          {/* Document Type Classification - NEW */}
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-white flex items-center gap-2">
                <Target className="text-amber-400" size={18} />
                Document Classification
              </h4>
              <span className="text-xs text-slate-500">
                {(deepAnalysisData.document_type_confidence * 100).toFixed(0)}% confidence
              </span>
            </div>
            
            <div className="flex items-center gap-3 mb-3">
              <span className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                deepAnalysisData.document_type === 'template' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                deepAnalysisData.document_type === 'transcript' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                deepAnalysisData.document_type === 'prompt_reference' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' :
                deepAnalysisData.document_type === 'knowledge_base' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                'bg-amber-500/20 text-amber-400 border border-amber-500/30'
              }`}>
                {deepAnalysisData.document_type?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown'}
              </span>
              
              <span className="text-xs text-slate-400">→</span>
              
              <span className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded">
                Recommended: {deepAnalysisData.recommended_destination?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Review Queue'}
              </span>
              
              {deepAnalysisData.destination_requires_review && (
                <span className="px-2 py-1 text-xs bg-amber-500/20 text-amber-400 rounded">
                  Needs Review
                </span>
              )}
            </div>
            
            {/* Document Type Evidence */}
            {deepAnalysisData.document_type_evidence?.length > 0 && (
              <div className="text-xs text-slate-500">
                Evidence: {deepAnalysisData.document_type_evidence.slice(0, 3).join(' • ')}
              </div>
            )}
          </div>
          
          {/* Summary Stats */}
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-white flex items-center gap-2">
                <CheckCircle className="text-emerald-400" size={18} />
                Analysis Complete
              </h4>
              <span className="text-xs text-slate-500">
                {deepAnalysisData.analysis_duration_ms}ms
              </span>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-stone-900 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-white">{deepAnalysisData.total_sections}</p>
                <p className="text-xs text-slate-400">Total Sections</p>
              </div>
              <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-400">{deepAnalysisData.useful_sections}</p>
                <p className="text-xs text-emerald-400/70">Useful</p>
              </div>
              <div className="bg-red-500/10 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-400">{deepAnalysisData.noise_sections}</p>
                <p className="text-xs text-red-400/70">Noise Flagged</p>
              </div>
            </div>
            
            {/* Overall Summary */}
            {deepAnalysisData.overall_summary && (
              <div className="mt-3 bg-stone-900 rounded-lg p-3">
                <p className="text-sm text-slate-300">{deepAnalysisData.overall_summary}</p>
              </div>
            )}
            
            {/* Training Signals */}
            {deepAnalysisData.training_signals && Object.keys(deepAnalysisData.training_signals).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(deepAnalysisData.training_signals).filter(([_, v]) => v).map(([key]) => (
                  <span key={key} className="px-2 py-1 text-xs bg-amber-500/20 text-amber-400 rounded">
                    {key.replace('has_', '').replace('_', ' ')}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {/* Improved Noise Detection - Type-Aware */}
          {(deepAnalysisData.noise_taxonomy?.length > 0 || deepAnalysisData.detected_names?.length > 0) && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
              <h4 className="font-medium text-amber-400 flex items-center gap-2 mb-2">
                <AlertTriangle size={16} />
                Noise Analysis {deepAnalysisData.document_type === 'template' && <span className="text-xs font-normal text-amber-400/60">(Template-aware)</span>}
              </h4>
              
              {/* New Noise Taxonomy Display */}
              {deepAnalysisData.noise_taxonomy?.length > 0 && (
                <div className="space-y-2 mb-3">
                  {deepAnalysisData.noise_taxonomy.map((noise, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        noise.severity === 'high' ? 'bg-red-500/20 text-red-300' :
                        noise.severity === 'medium' ? 'bg-amber-500/20 text-amber-300' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {noise.category}
                      </span>
                      <span className="text-xs text-slate-500">
                        ({(noise.confidence * 100).toFixed(0)}% confidence)
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* False Positive Warnings (Protected Terms) */}
              {deepAnalysisData.false_positive_warnings?.length > 0 && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2 mb-3">
                  <p className="text-xs text-emerald-400/80">
                    <Shield size={12} className="inline mr-1" />
                    {deepAnalysisData.false_positive_warnings[0]}
                  </p>
                </div>
              )}
              
              {/* Legacy speaker names display (only if actually detected) */}
              {deepAnalysisData.detected_names?.length > 0 && deepAnalysisData.document_type !== 'template' && (
                <div className="mb-2">
                  <p className="text-xs text-amber-400/70 mb-1">Speaker Labels:</p>
                  <div className="flex flex-wrap gap-1">
                    {deepAnalysisData.detected_names.slice(0, 5).map((name, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-300 rounded">{name}</span>
                    ))}
                    {deepAnalysisData.detected_names.length > 5 && (
                      <span className="text-xs text-amber-400">+{deepAnalysisData.detected_names.length - 5} more</span>
                    )}
                  </div>
                </div>
              )}
              
              {deepAnalysisData.detected_marketing?.length > 0 && (
                <div>
                  <p className="text-xs text-amber-400/70 mb-1">Marketing/Promo Content:</p>
                  <div className="flex flex-wrap gap-1">
                    {deepAnalysisData.detected_marketing.slice(0, 5).map((phrase, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-red-500/20 text-red-300 rounded">{phrase}</span>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-xs text-amber-400/50 mt-2">
                These items are flagged for review - they won't become training data unless explicitly approved.
              </p>
            </div>
          )}
          
          {/* Extracted Sections */}
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-white">Extracted Sections</h4>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">
                  {getSelectedSectionsCount()} of {deepAnalysisData.sections?.length || 0} selected
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowAllSections(!showAllSections)}
                >
                  {showAllSections ? 'Collapse' : 'Expand All'}
                </Button>
              </div>
            </div>
            
            <div className="space-y-3">
              {(deepAnalysisData.sections || []).map((section, idx) => {
                const typeConfig = SECTION_TYPE_CONFIG[section.section_type] || SECTION_TYPE_CONFIG.unknown;
                const selection = sectionSelections[section.section_id] || {};
                const dest = DESTINATIONS.find(d => d.value === selection.destination);
                const DestIcon = dest?.icon || FileText;
                
                return (
                  <div 
                    key={section.section_id}
                    className={`border rounded-lg overflow-hidden ${
                      section.is_noise 
                        ? 'border-red-500/30 bg-red-500/5' 
                        : selection.selected
                          ? 'border-amber-500/30 bg-stone-900'
                          : 'border-slate-700 bg-stone-900/50'
                    }`}
                    data-testid={`section-${section.section_id}`}
                  >
                    {/* Section Header */}
                    <div className="flex items-center justify-between p-3 bg-stone-800/50">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selection.selected}
                          onCheckedChange={(checked) => toggleSectionSelection(section.section_id, 'selected', checked)}
                          disabled={section.is_noise && section.recommended_action === 'reject'}
                        />
                        <div>
                          <h5 className="font-medium text-white text-sm">{section.title}</h5>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 text-xs rounded border ${typeConfig.color}`}>
                              {typeConfig.label}
                            </span>
                            {section.is_noise && (
                              <span className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 border border-red-500/30">
                                NOISE
                              </span>
                            )}
                            <span className="text-xs text-slate-500">
                              {section.word_count} words • {Math.round(section.confidence * 100)}% confidence
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Destination Selector */}
                      {selection.selected && !section.is_noise && (
                        <Select 
                          value={selection.destination} 
                          onValueChange={(v) => toggleSectionSelection(section.section_id, 'destination', v)}
                        >
                          <SelectTrigger className="w-40 bg-stone-800 border-slate-700 text-xs h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-stone-800 border-slate-700">
                            {DESTINATIONS.map(d => (
                              <SelectItem key={d.value} value={d.value} className="text-xs">
                                <div className="flex items-center gap-1">
                                  <d.icon size={12} className={d.color} />
                                  {d.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {/* View Full Section Button */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setViewingSection(section)}
                        className="text-slate-400 hover:text-white ml-2"
                        data-testid={`view-section-${section.section_id}`}
                      >
                        <Eye size={14} className="mr-1" />
                        View
                      </Button>
                    </div>
                    
                    {/* Section Preview Content (card view - compact) */}
                    {(showAllSections || idx < 3) && (
                      <div className="p-3 border-t border-stone-800">
                        <p className="text-sm text-slate-400 line-clamp-3">
                          {section.preview_excerpt || section.excerpt || section.content?.substring(0, 250)}
                        </p>
                        {section.key_points?.length > 0 && (
                          <div className="mt-2 pl-3 border-l-2 border-amber-500/30">
                            <p className="text-xs text-slate-500 mb-1">Key Points:</p>
                            {section.key_points.slice(0, 3).map((point, i) => (
                              <p key={i} className="text-xs text-slate-400">• {point}</p>
                            ))}
                            {section.key_points.length > 3 && (
                              <p className="text-xs text-slate-500 mt-1">+ {section.key_points.length - 3} more points</p>
                            )}
                          </div>
                        )}
                        {section.why_it_matters && (
                          <div className="mt-2 text-xs text-emerald-400 bg-emerald-500/10 rounded p-2">
                            <Lightbulb size={12} className="inline mr-1" />
                            {section.why_it_matters.substring(0, 150)}{section.why_it_matters.length > 150 ? '...' : ''}
                          </div>
                        )}
                        {section.is_noise && section.noise_reason && (
                          <p className="mt-2 text-xs text-red-400 bg-red-500/10 rounded p-2">
                            Flagged: {section.noise_reason}
                          </p>
                        )}
                        {section.word_count > 200 && (
                          <p className="mt-2 text-xs text-slate-500 text-center">
                            Click "View" to see full {section.word_count}-word section
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Creation Plan Summary */}
          {deepAnalysisData.creation_plan && (
            <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
              <h4 className="font-medium text-white mb-3">Downstream Creation Plan</h4>
              <div className="grid grid-cols-5 gap-2 text-center">
                {Object.entries(deepAnalysisData.creation_plan)
                  .filter(([key]) => key.endsWith('_count'))
                  .map(([key, count]) => {
                    const dest = DESTINATIONS.find(d => key.startsWith(d.value));
                    const DestIcon = dest?.icon || FileText;
                    return (
                      <div key={key} className="bg-stone-900 rounded-lg p-2">
                        <DestIcon size={16} className={`mx-auto mb-1 ${dest?.color || 'text-slate-400'}`} />
                        <p className="text-lg font-bold text-white">{count}</p>
                        <p className="text-xs text-slate-500">{dest?.label || key.replace('_count', '')}</p>
                      </div>
                    );
                  })}
              </div>
              <p className="text-xs text-slate-500 mt-3 text-center">
                Elements will be created in Draft status when you save the intake item.
              </p>
            </div>
          )}
          
          {/* Apply Button */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-700">
            <Button
              variant="outline"
              onClick={() => {
                setDeepAnalysisData(null);
                setSectionSelections({});
              }}
            >
              <XCircle size={14} className="mr-2" />
              Clear Analysis
            </Button>
            <Button
              onClick={() => onApplyDeepAnalysis(deepAnalysisData, sectionSelections)}
              className="bg-amber-600 hover:bg-amber-700"
              disabled={getSelectedSectionsCount() === 0}
              data-testid="apply-deep-analysis-btn"
            >
              <CheckCircle size={14} className="mr-2" />
              Apply Analysis ({getSelectedSectionsCount()} sections)
            </Button>
          </div>
        </div>
      )}
      
      {/* Full Section View Modal */}
      <Dialog open={viewingSection !== null} onOpenChange={(open) => !open && setViewingSection(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-stone-900 border-slate-700">
          {viewingSection && (
            <>
              <DialogHeader className="border-b border-slate-700 pb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl text-white">{viewingSection.title}</DialogTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-0.5 text-xs rounded border ${
                        SECTION_TYPE_CONFIG[viewingSection.section_type]?.color || 'text-slate-400 bg-slate-500/10'
                      }`}>
                        {SECTION_TYPE_CONFIG[viewingSection.section_type]?.label || viewingSection.section_type}
                      </span>
                      {viewingSection.is_noise && (
                        <span className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 border border-red-500/30">
                          NOISE
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        viewingSection.confidence > 0.8 ? 'bg-emerald-500/20 text-emerald-400' :
                        viewingSection.confidence > 0.5 ? 'bg-amber-500/20 text-amber-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {Math.round(viewingSection.confidence * 100)}% confidence
                      </span>
                      <span className="text-xs text-slate-500">
                        {viewingSection.word_count} words
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {DESTINATIONS.find(d => d.value === viewingSection.recommended_destination) && (
                      <span className="px-2 py-1 text-xs rounded bg-stone-800 border border-slate-700">
                        {(() => {
                          const dest = DESTINATIONS.find(d => d.value === viewingSection.recommended_destination);
                          const DestIcon = dest?.icon || FileText;
                          return (
                            <span className="flex items-center gap-1">
                              <DestIcon size={12} className={dest?.color} />
                              {dest?.label}
                            </span>
                          );
                        })()}
                      </span>
                    )}
                  </div>
                </div>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto py-4 space-y-4">
                {/* Why It Matters */}
                {viewingSection.why_it_matters && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb size={16} className="text-emerald-400" />
                      <h4 className="font-medium text-emerald-400">Why This Section Matters</h4>
                    </div>
                    <p className="text-sm text-slate-300">{viewingSection.why_it_matters}</p>
                  </div>
                )}
                
                {/* Full Extracted Body */}
                <div className="bg-stone-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                    <FileText size={16} className="text-amber-400" />
                    Full Extracted Content
                  </h4>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <div className="whitespace-pre-wrap text-sm text-slate-300 leading-relaxed">
                      {viewingSection.content}
                    </div>
                  </div>
                </div>
                
                {/* Key Points */}
                {viewingSection.key_points?.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <h4 className="font-medium text-amber-400 mb-3 flex items-center gap-2">
                      <Target size={16} />
                      Key Points ({viewingSection.key_points.length})
                    </h4>
                    <ul className="space-y-2">
                      {viewingSection.key_points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <span className="text-amber-400 mt-0.5">•</span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Key Rules / Thresholds */}
                {viewingSection.key_rules?.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <h4 className="font-medium text-amber-400 mb-3 flex items-center gap-2">
                      <AlertCircle size={16} />
                      Key Rules / Thresholds ({viewingSection.key_rules.length})
                    </h4>
                    <ul className="space-y-2">
                      {viewingSection.key_rules.map((rule, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <span className="text-amber-400 mt-0.5">→</span>
                          {rule}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Related Actions */}
                {viewingSection.related_actions?.length > 0 && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                    <h4 className="font-medium text-emerald-400 mb-3 flex items-center gap-2">
                      <ArrowRight size={16} />
                      Related Actions ({viewingSection.related_actions.length})
                    </h4>
                    <ul className="space-y-2">
                      {viewingSection.related_actions.map((action, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <span className="text-emerald-400 mt-0.5">→</span>
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Tags */}
                {viewingSection.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {viewingSection.tags.map((tag, i) => (
                      <span key={i} className="px-2 py-1 text-xs bg-stone-800 text-slate-300 rounded-full border border-slate-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Noise Info */}
                {viewingSection.is_noise && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <h4 className="font-medium text-red-400 mb-2 flex items-center gap-2">
                      <AlertTriangle size={16} />
                      Noise Detection
                    </h4>
                    {viewingSection.noise_reason && (
                      <p className="text-sm text-slate-300 mb-2">{viewingSection.noise_reason}</p>
                    )}
                    {viewingSection.noise_types?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {viewingSection.noise_types.map((type, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">
                            {type}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Metadata Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-700 text-xs text-slate-500">
                  <span>Section ID: {viewingSection.section_id}</span>
                  <span>Training Value: {viewingSection.training_value}</span>
                  <span>Recommended Action: {viewingSection.recommended_action}</span>
                </div>
              </div>
              
              <DialogFooter className="border-t border-slate-700 pt-4">
                <Button variant="outline" onClick={() => setViewingSection(null)}>
                  Close
                </Button>
                {!viewingSection.is_noise && (
                  <Button 
                    className="bg-amber-600 hover:bg-amber-700"
                    onClick={() => {
                      toggleSectionSelection(viewingSection.section_id, 'selected', true);
                      setViewingSection(null);
                      toast.success('Section selected for inclusion');
                    }}
                  >
                    <Check size={14} className="mr-2" />
                    Select Section
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Item card component
const IntakeItemCard = ({ item, onView, onEdit, onDuplicate, onPromote, onArchive, onRestore, onDelete, isSelected, onToggleSelect }) => {
  const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.uploaded;
  const StatusIcon = statusConfig.icon;
  const dest = DESTINATIONS.find(d => d.value === item.destination);
  const DestIcon = dest?.icon || FileText;
  const isArchived = item.status === 'archived';
  const isTestItem = item.title?.toLowerCase().startsWith('test') || item.notes?.toLowerCase()?.includes('test') || item.is_test;
  const isLowRisk = ['draft', 'uploaded', 'needs_review', 'rejected'].includes(item.status) && !item.promoted_to_id;

  return (
    <div 
      className={`bg-stone-900 border rounded-xl p-5 transition-all ${
        isSelected 
          ? 'border-amber-500 ring-2 ring-amber-500/20' 
          : isArchived 
            ? 'border-slate-700/50 opacity-75 hover:opacity-100 hover:border-slate-600'
            : 'border-stone-800 hover:border-amber-500/30'
      }`}
      data-testid={`intake-item-${item.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {/* Selection checkbox - event isolation: only onCheckedChange, no wrapper onClick */}
          <Checkbox 
            checked={isSelected}
            onCheckedChange={(checked) => {
              // Prevent event bubbling and call handler
              onToggleSelect?.(item.id);
            }}
            className={`h-5 w-5 border-2 shrink-0 ${isSelected ? 'border-amber-500 bg-amber-500' : 'border-slate-600'}`}
            data-testid={`select-item-${item.id}`}
          />
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            item.status === 'live' ? 'bg-amber-500/20' :
            item.status === 'approved' ? 'bg-emerald-500/20' :
            item.status === 'rejected' ? 'bg-red-500/20' :
            item.status === 'archived' ? 'bg-slate-700/50' : 'bg-slate-700'
          }`}>
            <StatusIcon className={
              item.status === 'live' ? 'text-amber-400' :
              item.status === 'approved' ? 'text-emerald-400' :
              item.status === 'rejected' ? 'text-red-400' :
              item.status === 'archived' ? 'text-slate-500' : 'text-slate-400'
            } size={20} />
          </div>
          <div>
            <h3 className={`font-semibold line-clamp-1 ${isArchived ? 'text-slate-400' : 'text-white'}`}>
              {item.title}
              {isTestItem && (
                <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-amber-500/20 text-amber-400 rounded">TEST</span>
              )}
            </h3>
            <p className="text-xs text-slate-500 capitalize">{item.content_type?.replace('_', ' ')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {item.ai_assist_mode && item.ai_assist_mode !== 'off' && (
            <span className="px-2 py-0.5 text-xs bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-full flex items-center gap-1">
              <Sparkles size={10} />
              AI
            </span>
          )}
          {item.is_duplicate && (
            <span className="px-2 py-0.5 text-xs bg-teal-500/10 text-teal-400 border border-teal-500/30 rounded-full">
              Duplicate
            </span>
          )}
          <span className={`px-2 py-1 text-xs rounded-full border ${statusConfig.color}`}>
            {statusConfig.label}
          </span>
        </div>
      </div>

      {/* AI Summary if present */}
      {item.ai_summary && (
        <p className={`text-sm mb-3 line-clamp-2 bg-amber-500/5 border border-amber-500/20 rounded-lg p-2 ${isArchived ? 'text-slate-500' : 'text-amber-300'}`}>
          <Sparkles size={12} className="inline mr-1" />
          {item.ai_summary}
        </p>
      )}

      {/* Preview snippet */}
      {!item.ai_summary && item.preview_snippet && (
        <p className={`text-sm mb-3 line-clamp-2 bg-stone-800/50 rounded-lg p-2 ${isArchived ? 'text-slate-500' : 'text-slate-400'}`}>
          {item.preview_snippet}
        </p>
      )}

      {/* Metadata */}
      <div className="flex flex-wrap gap-2 mb-3">
        {item.file_type && (
          <span className="px-2 py-0.5 text-xs bg-stone-800 text-slate-400 rounded uppercase">
            {item.file_type}
          </span>
        )}
        {item.word_count > 0 && (
          <span className="px-2 py-0.5 text-xs bg-stone-800 text-slate-400 rounded">
            {item.word_count} words
          </span>
        )}
        {item.source_type && (
          <span className="px-2 py-0.5 text-xs bg-stone-800 text-slate-400 rounded capitalize">
            {SOURCE_TYPE_LABELS[item.source_type] || item.source_type.replace('_', ' ')}
          </span>
        )}
        {item.promoted_to_id && (
          <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded flex items-center gap-1">
            <Link size={10} /> Linked
          </span>
        )}
      </div>

      {/* Tags */}
      {item.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {item.tags.slice(0, 3).map(tag => (
            <span key={tag} className="px-2 py-0.5 text-xs bg-amber-500/10 text-amber-400 rounded">
              {tag}
            </span>
          ))}
          {item.tags.length > 3 && (
            <span className="px-2 py-0.5 text-xs bg-stone-800 text-slate-500 rounded">
              +{item.tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Destination */}
      <div className="flex items-center gap-2 mb-3 text-sm">
        <ArrowRight size={14} className="text-slate-600" />
        <DestIcon size={14} className={dest?.color || 'text-slate-400'} />
        <span className="text-slate-400">{dest?.label || item.destination}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-stone-800">
        <span className="text-xs text-slate-500">
          {new Date(item.uploaded_at).toLocaleDateString()}
        </span>
        <div className="flex items-center gap-1">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => onView(item)} 
            title="View Full Content"
            className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
          >
            <Eye size={16} />
            <span className="ml-1 text-xs">View</span>
          </Button>
          {!isArchived && (
            <>
              <Button size="sm" variant="ghost" onClick={() => onEdit(item)} title="Edit">
                <Edit size={14} />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDuplicate(item)} title="Duplicate to Draft" className="text-teal-400">
                <Copy size={14} />
              </Button>
            </>
          )}
          {item.status === 'approved' && (
            <Button size="sm" variant="ghost" onClick={() => onPromote(item)} className="text-emerald-400" title="Promote to Live">
              <Send size={14} />
            </Button>
          )}
          {isArchived ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => onRestore(item)} className="text-emerald-400" title="Restore">
                <RotateCcw size={14} />
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => onDelete(item)} 
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10" 
                title="Delete permanently"
              >
                <Trash2 size={14} />
              </Button>
            </>
          ) : (
            <>
              {!['live'].includes(item.status) && (
                <Button size="sm" variant="ghost" onClick={() => onArchive(item)} className="text-slate-500" title="Archive">
                  <Archive size={14} />
                </Button>
              )}
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => onDelete(item)} 
                className={`${isLowRisk ? 'text-slate-500 hover:text-red-400' : 'text-amber-500 hover:text-red-400'} hover:bg-red-500/10`}
                title={isLowRisk ? "Delete" : "Delete (Protected)"}
              >
                {!isLowRisk && <Shield size={10} className="mr-0.5" />}
                <Trash2 size={14} />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Upload dialog component - ENHANCED with working AI Assist
const UploadDialog = ({ open, onClose, onSubmit }) => {
  const { getAuthHeaders } = useAuth();
  const [mode, setMode] = useState('text');
  const [formData, setFormData] = useState({
    title: '',
    content_type: 'general_knowledge',
    destination: 'knowledge_base',
    extracted_text: '',
    tags: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  
  // File extraction state
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionInfo, setExtractionInfo] = useState(null);
  
  // AI Assist state
  const [aiAssistMode, setAiAssistMode] = useState('off');
  const [aiTasks, setAiTasks] = useState({
    classify: false, tags: false, destinations: false, summary: false, keypoints: false,
  });
  const [suggestions, setSuggestions] = useState(null);
  const [acceptedSuggestions, setAcceptedSuggestions] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState(null);
  const [aiSummary, setAiSummary] = useState('');
  const [aiKeyPoints, setAiKeyPoints] = useState([]);
  
  // Deep Analysis state
  const [deepAnalysisData, setDeepAnalysisData] = useState(null);
  const [processingStage, setProcessingStage] = useState(null);
  const [sectionSelections, setSectionSelections] = useState({});

  const handleApplyDeepAnalysis = (analysisData, selections) => {
    // Apply overall analysis to form
    if (analysisData.overall_content_type) {
      setFormData(prev => ({ ...prev, content_type: analysisData.overall_content_type }));
    }
    if (analysisData.overall_destination) {
      setFormData(prev => ({ ...prev, destination: analysisData.overall_destination }));
    }
    if (analysisData.all_tags?.length > 0) {
      setFormData(prev => ({ ...prev, tags: analysisData.all_tags.join(', ') }));
    }
    if (analysisData.overall_summary) {
      setAiSummary(analysisData.overall_summary);
    }
    if (analysisData.key_doctrine_points?.length > 0) {
      setAiKeyPoints(analysisData.key_doctrine_points);
    }
    
    // Store the full analysis data and selections for save
    setAcceptedSuggestions({
      ...acceptedSuggestions,
      deep_analysis: true,
      selected_sections: Object.entries(selections)
        .filter(([_, s]) => s.selected)
        .map(([id, s]) => ({ section_id: id, ...s }))
    });
    
    toast.success('Deep analysis applied - ready to save');
  };

  const handleApplySuggestion = (type, value) => {
    if (type === 'content_type') {
      setFormData(prev => ({ ...prev, content_type: value }));
    } else if (type === 'destination') {
      setFormData(prev => ({ ...prev, destination: value }));
    } else if (type === 'tags') {
      setFormData(prev => ({ ...prev, tags: Array.isArray(value) ? value.join(', ') : value }));
    } else if (type === 'summary') {
      setAiSummary(value);
    } else if (type === 'keypoints') {
      setAiKeyPoints(value);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.extracted_text) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      // Build rejected suggestions
      const rejectedSuggestions = {};
      if (suggestions) {
        Object.keys(suggestions).forEach(key => {
          const taskKey = key === 'content_type' ? 'classify' : 
                         key === 'key_points' ? 'keypoints' : key;
          if (acceptedSuggestions[taskKey] === false) {
            rejectedSuggestions[taskKey] = true;
          }
        });
      }
      
      await onSubmit({
        ...formData,
        source_type: mode === 'text' ? 'pasted_text' : 'file_upload',
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        ai_assist_mode: aiAssistMode,
        ai_assist_suggestions: suggestions,
        ai_assist_accepted: acceptedSuggestions,
        ai_assist_rejected: rejectedSuggestions,
        // Include deep analysis data if available
        deep_analysis_data: deepAnalysisData,
        ai_summary: aiSummary,
        ai_key_points: aiKeyPoints,
      });
      toast.success('Content added to intake');
      onClose();
      // Reset form
      setFormData({
        title: '', content_type: 'general_knowledge', destination: 'knowledge_base',
        extracted_text: '', tags: '', notes: '',
      });
      setAiAssistMode('off');
      setSuggestions(null);
      setAcceptedSuggestions({});
      setAiSummary('');
      setAiKeyPoints([]);
      setDeepAnalysisData(null);
      setSectionSelections({});
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFileRead = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    // Text files can be read directly
    if (['txt', 'md'].includes(fileExtension)) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result;
        setFormData(prev => ({
          ...prev,
          title: prev.title || file.name.replace(/\.[^/.]+$/, ''),
          extracted_text: content,
          file_type: fileExtension,
          original_filename: file.name,
        }));
        setExtractionInfo({
          method: 'direct_read',
          status: 'success',
          confidence: 1.0,
          warnings: []
        });
        toast.success(`File loaded: ${file.name}`);
      };
      reader.onerror = () => toast.error('Failed to read file');
      reader.readAsText(file);
      return;
    }
    
    // Binary files (PDF, DOCX, DOC) need server-side extraction
    if (['pdf', 'docx', 'doc'].includes(fileExtension)) {
      setIsExtracting(true);
      setExtractionInfo(null);
      setFormData(prev => ({
        ...prev,
        title: prev.title || file.name.replace(/\.[^/.]+$/, ''),
        extracted_text: '',
        file_type: fileExtension,
        original_filename: file.name,
      }));
      
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${API_URL}/api/admin-ops/training-intake/extract-file`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
          },
          body: formDataUpload,
        });
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Extraction failed' }));
          throw new Error(error.detail || 'Failed to extract file content');
        }
        
        const result = await response.json();
        
        setExtractionInfo({
          method: result.extraction_method,
          status: result.extraction_status,
          confidence: result.extraction_confidence,
          warnings: result.extraction_warnings || [],
          wordCount: result.word_count,
          charCount: result.char_count,
        });
        
        if (result.success && result.extracted_text) {
          setFormData(prev => ({
            ...prev,
            extracted_text: result.extracted_text,
          }));
          
          if (result.extraction_warnings?.length > 0) {
            toast.warning(`File extracted with warnings: ${result.extraction_warnings[0]}`);
          } else {
            toast.success(`File extracted: ${file.name} (${result.word_count} words)`);
          }
        } else {
          const warningMsg = result.extraction_warnings?.[0] || 'Could not extract readable text from this file.';
          toast.error(warningMsg);
          setFormData(prev => ({
            ...prev,
            extracted_text: '',
          }));
        }
      } catch (error) {
        console.error('File extraction error:', error);
        toast.error(error.message || 'Failed to extract file content');
        setExtractionInfo({
          method: 'failed',
          status: 'failed',
          confidence: 0,
          warnings: [error.message || 'Extraction failed'],
        });
      } finally {
        setIsExtracting(false);
      }
      return;
    }
    
    // Unsupported file type
    toast.error(`Unsupported file type: .${fileExtension}. Please upload PDF, DOCX, DOC, TXT, or MD files.`);
  };

  return (
    <DialogContent className="max-w-6xl bg-stone-900 border-stone-800 text-white max-h-[95vh]">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Upload className="text-amber-400" size={20} />
          Add Training Material
        </DialogTitle>
        <DialogDescription className="text-slate-400">
          Upload or paste content to add to the training intake pipeline.
        </DialogDescription>
      </DialogHeader>

      {/* Mode tabs */}
      <div className="flex gap-2 border-b border-stone-800 pb-4">
        <button
          onClick={() => setMode('text')}
          className={`px-4 py-2 rounded-lg text-sm transition-all ${
            mode === 'text' ? 'bg-amber-600 text-white' : 'bg-stone-800 text-slate-400 hover:bg-slate-700'
          }`}
        >
          Paste Text
        </button>
        <button
          onClick={() => setMode('file')}
          className={`px-4 py-2 rounded-lg text-sm transition-all ${
            mode === 'file' ? 'bg-amber-600 text-white' : 'bg-stone-800 text-slate-400 hover:bg-slate-700'
          }`}
        >
          Upload File
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6 max-h-[70vh] overflow-y-auto py-4">
        {/* Left column - Form fields */}
        <div className="col-span-5 space-y-4">
          {/* File upload zone */}
          {mode === 'file' && (
            <div className="space-y-2">
              <div
                onClick={() => !isExtracting && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                  isExtracting 
                    ? 'border-amber-500/50 bg-amber-500/5 cursor-wait' 
                    : 'border-slate-700 cursor-pointer hover:border-amber-500/50'
                }`}
              >
                {isExtracting ? (
                  <>
                    <Loader2 className="mx-auto text-amber-400 mb-2 animate-spin" size={28} />
                    <p className="text-amber-400 text-sm">Extracting text from file...</p>
                    <p className="text-xs text-slate-500">This may take a moment for large PDFs</p>
                  </>
                ) : (
                  <>
                    <Upload className="mx-auto text-slate-500 mb-2" size={28} />
                    <p className="text-slate-400 text-sm">Click to upload</p>
                    <p className="text-xs text-slate-500">TXT, PDF, DOCX</p>
                  </>
                )}
                <input ref={fileInputRef} type="file" accept=".txt,.pdf,.docx,.doc,.md" onChange={handleFileRead} className="hidden" />
              </div>
              
              {/* Extraction status info */}
              {extractionInfo && (
                <div className={`rounded-lg p-3 text-xs ${
                  extractionInfo.status === 'success' 
                    ? 'bg-emerald-900/20 border border-emerald-600/30' 
                    : extractionInfo.status === 'partial'
                    ? 'bg-amber-900/20 border border-amber-600/30'
                    : 'bg-red-900/20 border border-red-600/30'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    {extractionInfo.status === 'success' && <CheckCircle size={14} className="text-emerald-400" />}
                    {extractionInfo.status === 'partial' && <AlertCircle size={14} className="text-amber-400" />}
                    {extractionInfo.status === 'failed' && <AlertCircle size={14} className="text-red-400" />}
                    <span className={
                      extractionInfo.status === 'success' ? 'text-emerald-400' :
                      extractionInfo.status === 'partial' ? 'text-amber-400' : 'text-red-400'
                    }>
                      {extractionInfo.status === 'success' ? 'Extracted successfully' :
                       extractionInfo.status === 'partial' ? 'Partial extraction' : 'Extraction failed'}
                    </span>
                    {extractionInfo.method && (
                      <span className="text-slate-500">
                        ({extractionInfo.method === 'ocr' ? 'OCR' : 
                          extractionInfo.method === 'hybrid' ? 'Text + OCR' :
                          extractionInfo.method === 'pymupdf' ? 'PDF Text' :
                          extractionInfo.method === 'docx' ? 'DOCX' :
                          extractionInfo.method})
                      </span>
                    )}
                  </div>
                  {extractionInfo.wordCount > 0 && (
                    <p className="text-slate-400">
                      {extractionInfo.wordCount.toLocaleString()} words extracted
                    </p>
                  )}
                  {extractionInfo.warnings?.length > 0 && (
                    <div className="mt-1 text-amber-400/80">
                      {extractionInfo.warnings.map((w, i) => (
                        <p key={i}>⚠ {w}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <Label className="text-sm text-slate-400 mb-1 block">Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter a descriptive title..."
              className="bg-stone-800 border-slate-700 text-white"
              data-testid="intake-title-input"
            />
          </div>

          {/* Content type & Destination */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-slate-400 mb-1 block">
                Content Type *
                {acceptedSuggestions.classify && <Sparkles size={10} className="inline ml-1 text-amber-400" />}
              </Label>
              <Select value={formData.content_type} onValueChange={(value) => setFormData(prev => ({ ...prev, content_type: value }))}>
                <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="intake-content-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-stone-800 border-slate-700">
                  {CONTENT_TYPES.map(ct => (
                    <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-slate-400 mb-1 block">
                Destination *
                {acceptedSuggestions.destinations && <Sparkles size={10} className="inline ml-1 text-amber-400" />}
                <span className="ml-2 text-xs text-slate-600 font-normal" title="Promotion creates an independent copy in the destination system. Changes made later won't sync back.">
                  (creates copy)
                </span>
              </Label>
              <Select value={formData.destination} onValueChange={(value) => setFormData(prev => ({ ...prev, destination: value }))}>
                <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="intake-destination-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-stone-800 border-slate-700">
                  {DESTINATIONS.map(d => (
                    <SelectItem key={d.value} value={d.value}>
                      <div className="flex items-center gap-2">
                        <d.icon size={14} className={d.color} />
                        <div>
                          <span>{d.label}</span>
                          {d.description && (
                            <span className="text-xs text-slate-500 ml-2 hidden md:inline">— {d.description}</span>
                          )}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-600 mt-1">
                Promoted items become independent copies — no ongoing sync with intake.
              </p>
            </div>
          </div>

          {/* Content */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm text-slate-400">Content *</Label>
              <span className="text-xs text-slate-500">{formData.extracted_text.length} chars</span>
            </div>
            <Textarea
              value={formData.extracted_text}
              onChange={(e) => setFormData(prev => ({ ...prev, extracted_text: e.target.value }))}
              placeholder="Paste or enter your content here..."
              className="bg-stone-800 border-slate-700 text-white resize-none h-32 font-mono text-sm"
              data-testid="intake-content-textarea"
            />
          </div>

          {/* Tags */}
          <div>
            <Label className="text-sm text-slate-400 mb-1 block">
              Tags (comma separated)
              {acceptedSuggestions.tags && <Sparkles size={10} className="inline ml-1 text-amber-400" />}
            </Label>
            <Input
              value={formData.tags}
              onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
              placeholder="metro2, collections, balance..."
              className="bg-stone-800 border-slate-700 text-white"
              data-testid="intake-tags-input"
            />
          </div>

          {/* Notes */}
          <div>
            <Label className="text-sm text-slate-400 mb-1 block">Notes (optional)</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional notes..."
              className="bg-stone-800 border-slate-700 text-white"
            />
          </div>
        </div>

        {/* Middle column - AI Assist / Deep Analysis */}
        <div className="col-span-4">
          {aiAssistMode === 'deep' ? (
            <DeepAnalysisPanel
              content={formData.extracted_text}
              title={formData.title}
              deepAnalysisData={deepAnalysisData}
              setDeepAnalysisData={setDeepAnalysisData}
              isProcessing={isProcessing}
              setIsProcessing={setIsProcessing}
              processingStage={processingStage}
              setProcessingStage={setProcessingStage}
              sectionSelections={sectionSelections}
              setSectionSelections={setSectionSelections}
              onApplyDeepAnalysis={handleApplyDeepAnalysis}
              onSwitchToStandard={() => setAiAssistMode('full')}
            />
          ) : (
            <AIAssistPanel
              aiAssistMode={aiAssistMode}
              setAiAssistMode={setAiAssistMode}
              aiTasks={aiTasks}
              setAiTasks={setAiTasks}
              content={formData.extracted_text}
              title={formData.title}
              currentContentType={formData.content_type}
              currentDestination={formData.destination}
              currentTags={formData.tags ? formData.tags.split(',').map(t => t.trim()) : []}
              suggestions={suggestions}
              setSuggestions={setSuggestions}
              acceptedSuggestions={acceptedSuggestions}
              setAcceptedSuggestions={setAcceptedSuggestions}
              isProcessing={isProcessing}
              setIsProcessing={setIsProcessing}
              processingError={processingError}
              setProcessingError={setProcessingError}
              onApplySuggestion={handleApplySuggestion}
            />
          )}
        </div>

        {/* Right column - Processing Impact */}
        <div className="col-span-3">
          <ProcessingImpactPreview
            aiAssistMode={aiAssistMode}
            aiTasks={aiTasks}
            destination={formData.destination}
            contentType={formData.content_type}
            acceptedSuggestions={acceptedSuggestions}
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving || isProcessing} data-testid="intake-submit-btn">
          {saving ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
          Add to Intake
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

// Item detail/edit dialog - simplified for this update
const ItemDetailDialog = ({ item, onClose, onUpdate, onPromote, onDuplicate, onRestore, mode = 'view' }) => {
  const [currentMode, setCurrentMode] = useState(mode);
  const [formData, setFormData] = useState({
    title: item?.title || '',
    content_type: item?.content_type || '',
    destination: item?.destination || '',
    extracted_text: item?.extracted_text || '',
    tags: item?.tags?.join(', ') || '',
    notes: item?.notes || '',
    reviewer_notes: item?.reviewer_notes || '',
    status: item?.status || '',
  });
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);

  useEffect(() => {
    setCurrentMode(mode);
    if (item) {
      setFormData({
        title: item.title || '',
        content_type: item.content_type || '',
        destination: item.destination || '',
        extracted_text: item.extracted_text || '',
        tags: item.tags?.join(', ') || '',
        notes: item.notes || '',
        reviewer_notes: item.reviewer_notes || '',
        status: item.status || '',
      });
    }
  }, [item, mode]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(item.id, {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      });
      toast.success('Item updated');
      setCurrentMode('view');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    try {
      await onUpdate(item.id, { status: newStatus, reviewer_notes: formData.reviewer_notes });
      toast.success(`Status changed to ${newStatus}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Promote handler with loading state
  const handlePromoteClick = async () => {
    setPromoting(true);
    try {
      await onPromote(item);
    } finally {
      setPromoting(false);
    }
  };

  if (!item) return null;

  const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.uploaded;
  const dest = DESTINATIONS.find(d => d.value === item.destination);
  const DestIcon = dest?.icon || FileText;
  const isArchived = item.status === 'archived';
  const isEditing = currentMode === 'edit';

  return (
    <DialogContent className="max-w-5xl bg-stone-900 border-stone-800 text-white max-h-[95vh]">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {isEditing ? <Edit className="text-amber-400" size={20} /> : <Eye className="text-amber-400" size={20} />}
          {isEditing ? 'Edit Item' : item.title}
        </DialogTitle>
        <DialogDescription className="text-slate-400 flex items-center gap-2">
          {CONTENT_TYPES.find(c => c.value === item.content_type)?.label || item.content_type}
          <span className={`px-2 py-0.5 rounded text-xs border ${statusConfig.color}`}>{statusConfig.label}</span>
          {item.ai_assist_mode && item.ai_assist_mode !== 'off' && (
            <span className="px-2 py-0.5 text-xs bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded flex items-center gap-1">
              <Sparkles size={10} /> AI Assisted
            </span>
          )}
        </DialogDescription>
      </DialogHeader>

      <Tabs defaultValue="content" className="w-full">
        <TabsList className="bg-stone-800 mb-4">
          <TabsTrigger value="content"><FileText size={14} className="mr-1" /> Content</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          {item.ai_assist_data && <TabsTrigger value="ai-data">AI Data</TabsTrigger>}
        </TabsList>

        <div className="max-h-[60vh] overflow-y-auto">
          <TabsContent value="content">
            <div className="space-y-4">
              {item.ai_summary && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <p className="text-xs text-amber-400 mb-2 flex items-center gap-1"><Sparkles size={12} /> AI Summary</p>
                  <p className="text-sm text-slate-300">{item.ai_summary}</p>
                </div>
              )}
              
              {item.ai_key_points?.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <p className="text-xs text-amber-400 mb-2 flex items-center gap-1"><Lightbulb size={12} /> AI Key Points</p>
                  <ul className="space-y-1">
                    {item.ai_key_points.map((point, idx) => (
                      <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                        <span className="text-amber-400">•</span> {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {isEditing ? (
                <Textarea
                  value={formData.extracted_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, extracted_text: e.target.value }))}
                  className="bg-stone-950 border-slate-700 text-white resize-none font-mono text-sm"
                  rows={20}
                />
              ) : (
                <div className="bg-stone-950 border border-stone-800 rounded-lg p-6 min-h-[300px] max-h-[50vh] overflow-y-auto">
                  <pre className="text-sm text-slate-200 whitespace-pre-wrap font-mono">{item.extracted_text || 'No content'}</pre>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="details">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                {isEditing ? (
                  <>
                    <div>
                      <Label className="text-slate-400">Title</Label>
                      <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} className="bg-stone-800 border-slate-700 text-white mt-1" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-400">Content Type</Label>
                        <Select value={formData.content_type} onValueChange={(value) => setFormData(prev => ({ ...prev, content_type: value }))}>
                          <SelectTrigger className="bg-stone-800 border-slate-700 text-white mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-stone-800 border-slate-700">
                            {CONTENT_TYPES.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-400">Destination</Label>
                        <Select value={formData.destination} onValueChange={(value) => setFormData(prev => ({ ...prev, destination: value }))}>
                          <SelectTrigger className="bg-stone-800 border-slate-700 text-white mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-stone-800 border-slate-700">
                            {DESTINATIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-400">Tags</Label>
                      <Input value={formData.tags} onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))} className="bg-stone-800 border-slate-700 text-white mt-1" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-stone-800/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Content Type</p>
                      <p className="text-white">{CONTENT_TYPES.find(c => c.value === item.content_type)?.label || item.content_type}</p>
                    </div>
                    <div className="bg-stone-800/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Destination</p>
                      <div className="flex items-center gap-2">
                        <DestIcon size={16} className={dest?.color || 'text-slate-400'} />
                        <span className="text-white">{dest?.label || item.destination}</span>
                      </div>
                    </div>
                    {item.tags?.length > 0 && (
                      <div className="bg-stone-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-2">Tags</p>
                        <div className="flex flex-wrap gap-1">
                          {item.tags.map(tag => <span key={tag} className="px-2 py-1 text-xs bg-amber-500/20 text-amber-400 rounded">{tag}</span>)}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div className="space-y-4">
                <div className="bg-stone-800 rounded-lg p-4 space-y-3">
                  <h4 className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1"><Calendar size={12} /> Timestamps</h4>
                  <div>
                    <p className="text-xs text-slate-500">Uploaded</p>
                    <p className="text-sm text-white font-mono">{formatUTCTimestamp(item.uploaded_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">By</p>
                    <p className="text-sm text-white">{item.uploaded_by}</p>
                  </div>
                </div>
                
                {!isArchived && !['live'].includes(item.status) && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500">Change Status</p>
                    <div className="grid grid-cols-2 gap-2">
                      {item.status !== 'needs_review' && <Button size="sm" variant="outline" onClick={() => handleStatusChange('needs_review')} disabled={saving}>Needs Review</Button>}
                      {item.status !== 'approved' && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleStatusChange('approved')} disabled={saving}>Approve</Button>}
                    </div>
                  </div>
                )}
                
                {item.status === 'approved' && (
                  <Button 
                    className="w-full bg-amber-600 hover:bg-amber-700" 
                    onClick={handlePromoteClick} 
                    disabled={saving || promoting}
                    data-testid="promote-to-live-btn"
                  >
                    {promoting ? (
                      <><Loader2 size={16} className="mr-2 animate-spin" /> Promoting...</>
                    ) : (
                      <><Send size={16} className="mr-2" /> Promote to Live</>
                    )}
                  </Button>
                )}
                
                {item.status === 'live' && (
                  <div className="w-full px-4 py-2 bg-emerald-900/30 border border-emerald-600/40 rounded-md text-center text-emerald-400 text-sm">
                    <CheckCircle size={16} className="inline mr-2" />
                    Already Live
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history">
            <div className="space-y-3">
              {item.status_history?.slice().reverse().map((event, idx) => {
                const eventConfig = STATUS_CONFIG[event.status] || STATUS_CONFIG.uploaded;
                return (
                  <div key={idx} className="bg-stone-800/50 rounded-lg p-4 flex items-start gap-4">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${eventConfig.color.replace('text-', 'bg-').replace('-400', '-500/20')}`}>
                      {React.createElement(eventConfig.icon, { size: 16, className: eventConfig.color.split(' ')[1] })}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`px-2 py-0.5 text-xs rounded border ${eventConfig.color}`}>{eventConfig.label}</span>
                        <span className="text-xs text-slate-500 font-mono">{formatUTCTimestamp(event.changed_at)}</span>
                      </div>
                      <p className="text-sm text-slate-400">By: {event.changed_by}</p>
                      {event.notes && <p className="text-sm text-slate-500 italic mt-1">{event.notes}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {item.ai_assist_data && (
            <TabsContent value="ai-data">
              <div className="space-y-4">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2"><Sparkles size={14} /> AI Assist Data</h4>
                  
                  {item.ai_assist_data.suggestions && (
                    <div className="space-y-3">
                      <p className="text-xs text-slate-400">Suggestions Generated</p>
                      <pre className="text-xs text-slate-300 bg-stone-800 rounded p-3 overflow-auto max-h-48">
                        {JSON.stringify(item.ai_assist_data.suggestions, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {item.ai_assist_data.accepted && Object.keys(item.ai_assist_data.accepted).length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-emerald-400 mb-2">Accepted Suggestions</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(item.ai_assist_data.accepted).filter(([_, v]) => v).map(([key]) => (
                          <span key={key} className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded">{key}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {item.ai_assist_data.rejected && Object.keys(item.ai_assist_data.rejected).length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-slate-400 mb-2">Rejected Suggestions</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(item.ai_assist_data.rejected).filter(([_, v]) => v).map(([key]) => (
                          <span key={key} className="px-2 py-0.5 text-xs bg-slate-500/20 text-slate-400 rounded">{key}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          )}
        </div>
      </Tabs>

      <DialogFooter className="gap-2">
        {isEditing ? (
          <>
            <Button variant="outline" onClick={() => setCurrentMode('view')}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? <RefreshCw size={16} className="mr-2 animate-spin" /> : null} Save</Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onClose}>Close</Button>
            {!isArchived && (
              <>
                <Button variant="outline" onClick={() => onDuplicate(item)}><Copy size={16} className="mr-2" /> Duplicate</Button>
                <Button variant="outline" onClick={() => setCurrentMode('edit')}><Edit size={16} className="mr-2" /> Edit</Button>
              </>
            )}
          </>
        )}
      </DialogFooter>
    </DialogContent>
  );
};

// Main page component
const TrainingIntakePage = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [contentTypeFilter, setContentTypeFilter] = useState('all');
  const [destinationFilter, setDestinationFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusCounts, setStatusCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [showUpload, setShowUpload] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [viewMode, setViewMode] = useState('view');
  const [showArchived, setShowArchived] = useState(false);
  
  // 2FA state for promote action
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [pending2FAItem, setPending2FAItem] = useState(null);
  
  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [delete2FACode, setDelete2FACode] = useState('');
  const [deleteLinked, setDeleteLinked] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePending2FA, setDeletePending2FA] = useState(false);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkActionDialog, setShowBulkActionDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkActionResults, setBulkActionResults] = useState(null);
  
  // Bulk 2FA state - for handling 2FA in bulk actions
  const [bulk2FARequired, setBulk2FARequired] = useState(false);
  const [bulk2FACode, setBulk2FACode] = useState('');
  const [bulk2FAError, setBulk2FAError] = useState('');
  const [bulk2FAVerifying, setBulk2FAVerifying] = useState(false);
  const [pendingBulkItems, setPendingBulkItems] = useState([]); // Items pending 2FA verification
  const [partialBulkResults, setPartialBulkResults] = useState(null); // Partial results before 2FA was required

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (contentTypeFilter !== 'all') params.content_type = contentTypeFilter;
      if (destinationFilter !== 'all') params.destination = destinationFilter;
      if (showArchived || statusFilter === 'archived') params.include_archived = 'true';
      
      const data = await adminOpsService.listTrainingIntake(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStatusCounts(data.status_counts || {});
    } catch (error) {
      toast.error('Failed to load training intake');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, statusFilter, contentTypeFilter, destinationFilter, showArchived]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleCreate = async (itemData) => {
    adminOpsService.setAuthHeaders(getAuthHeaders());
    await adminOpsService.createTrainingIntake(itemData);
    fetchItems();
  };

  const handleUpdate = async (itemId, updates) => {
    adminOpsService.setAuthHeaders(getAuthHeaders());
    await adminOpsService.updateTrainingIntake(itemId, updates);
    fetchItems();
    if (viewingItem?.id === itemId) {
      const updatedItems = await adminOpsService.listTrainingIntake({ include_archived: 'true' });
      const updated = updatedItems.items?.find(i => i.id === itemId);
      if (updated) setViewingItem(updated);
    }
  };

  const handlePromote = async (item, twoFAToken = null) => {
    if (!twoFAToken && !window.confirm(`Promote "${item.title}" to ${item.destination.replace('_', ' ')}?\n\nThis will make the content LIVE.`)) return;
    
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      await adminOpsService.promoteTrainingIntake(item.id, twoFAToken);
      toast.success(`Promoted to ${item.destination.replace('_', ' ')}`);
      fetchItems();
      setViewingItem(null);
      setPending2FAItem(null);
    } catch (error) {
      // Check if 2FA is required
      console.log('Promote error:', error, 'requires2FA:', error.requires2FA);
      if (error.requires2FA) {
        console.log('Opening 2FA modal for item:', item.id);
        setPending2FAItem(item);
        setShow2FAModal(true);
        return;
      }
      toast.error(error.message);
    }
  };
  
  const handle2FAVerified = async (token) => {
    if (pending2FAItem) {
      await handlePromote(pending2FAItem, token);
    }
    setShow2FAModal(false);
  };

  const handleArchive = async (item) => {
    const reason = window.prompt(`Archive "${item.title}"?\n\nOptionally provide a reason:`);
    if (reason === null) return;
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      await adminOpsService.archiveTrainingIntake(item.id, reason || undefined);
      toast.success('Item archived');
      fetchItems();
      if (viewingItem?.id === item.id) setViewingItem(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRestore = async (item) => {
    if (!window.confirm(`Restore "${item.title}"?`)) return;
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      await adminOpsService.restoreTrainingIntake(item.id);
      toast.success('Item restored to Draft');
      fetchItems();
      if (viewingItem?.id === item.id) setViewingItem(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDuplicate = async (item) => {
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const response = await adminOpsService.duplicateTrainingIntake(item.id);
      toast.success('Item duplicated to Draft');
      fetchItems();
      if (response?.item) {
        setViewingItem(response.item);
        setViewMode('edit');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Delete handlers
  const handleDeleteClick = async (item) => {
    setDeleteTarget(item);
    setDeleteReason('');
    setDelete2FACode('');
    setDeleteLinked(false);
    setDeletePending2FA(false);
    setDeleteLoading(true);
    
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const info = await adminOpsService.getTrainingIntakeDeleteInfo(item.id);
      setDeleteInfo(info);
      setShowDeleteDialog(true);
    } catch (error) {
      toast.error('Failed to get delete info: ' + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteConfirm = async (twoFAToken = null) => {
    if (!deleteTarget) return;
    
    const info = deleteInfo;
    
    // Validate reason if required
    if (info?.requires_reason && (!deleteReason || deleteReason.trim().length < 5)) {
      toast.error('Please provide a reason (minimum 5 characters)');
      return;
    }
    
    setDeleteLoading(true);
    
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      
      // Get 2FA token if needed and not provided
      let token = twoFAToken;
      if (info?.requires_2fa && delete2FACode && !token) {
        try {
          const verifyResult = await adminOpsService.verify2FA(
            delete2FACode,
            'intake_delete',
            deleteTarget.id
          );
          // Backend returns 'success' not 'verified'
          if ((verifyResult.success || verifyResult.verified) && verifyResult.token) {
            token = verifyResult.token;
          } else {
            toast.error(verifyResult.error || '2FA verification failed - invalid response');
            setDeleteLoading(false);
            return;
          }
        } catch (verifyError) {
          // Handle verification errors without clearing form state
          toast.error(verifyError.message || '2FA verification failed');
          setDeleteLoading(false);
          return;
        }
      }
      
      const result = await adminOpsService.deleteTrainingIntake(deleteTarget.id, {
        reason: deleteReason || undefined,
        deleteLinked,
        force: info?.risk_level === 'high',
        twoFAToken: token
      });
      
      if (result.success) {
        toast.success(`Deleted "${deleteTarget.title}"`);
        setShowDeleteDialog(false);
        setDeleteTarget(null);
        setDeleteInfo(null);
        fetchItems();
        if (viewingItem?.id === deleteTarget.id) {
          setViewingItem(null);
        }
      }
    } catch (error) {
      if (error.requires2FA) {
        setDeletePending2FA(true);
        toast.error('2FA verification required');
      } else if (error.requiresForce) {
        toast.error(error.message);
      } else {
        toast.error(error.message || 'Delete failed');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDelete2FAVerified = async (token) => {
    setDeletePending2FA(false);
    await handleDeleteConfirm(token);
  };

  // ==================== BULK SELECTION HANDLERS ====================
  // Note: These handlers are defined here but the ones that need filteredItems 
  // are defined after filteredItems is calculated
  const handleToggleSelect = (itemId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter, contentTypeFilter, destinationFilter, searchQuery]);

  const filteredItems = items.filter(item => {
    const matchesSearch = item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.extracted_text?.toLowerCase().includes(searchQuery.toLowerCase());
    if (showArchived && statusFilter === 'all') return matchesSearch && item.status === 'archived';
    return matchesSearch;
  });

  // ==================== BULK SELECTION HANDLERS (need filteredItems) ====================
  
  // Compute visible selected items (intersection of selectedIds and filteredItems)
  const visibleSelectedIds = new Set(
    filteredItems.filter(item => selectedIds.has(item.id)).map(item => item.id)
  );
  const visibleSelectedCount = visibleSelectedIds.size;
  const allVisibleSelected = filteredItems.length > 0 && visibleSelectedCount === filteredItems.length;
  const someVisibleSelected = visibleSelectedCount > 0 && visibleSelectedCount < filteredItems.length;
  const noneSelected = visibleSelectedCount === 0;

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      // Unselect all visible items (keep non-visible selections)
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        filteredItems.forEach(item => newSet.delete(item.id));
        return newSet;
      });
    } else {
      // Select all visible items (add to existing selections)
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        filteredItems.forEach(item => newSet.add(item.id));
        return newSet;
      });
    }
  };

  const selectedItems = filteredItems.filter(item => selectedIds.has(item.id));

  const openBulkActionDialog = (action) => {
    setBulkAction(action);
    setBulkActionResults(null);
    setBulk2FARequired(false);
    setBulk2FACode('');
    setBulk2FAError('');
    setPendingBulkItems([]);
    setPartialBulkResults(null);
    setShowBulkActionDialog(true);
  };

  const handleBulkApprove = async () => {
    setBulkActionLoading(true);
    const results = { succeeded: [], skipped: [], failed: [] };
    
    for (const item of selectedItems) {
      try {
        if (!['draft', 'needs_review', 'uploaded'].includes(item.status)) {
          results.skipped.push({ item, reason: `Already ${item.status}` });
          continue;
        }
        adminOpsService.setAuthHeaders(getAuthHeaders());
        await adminOpsService.updateTrainingIntake(item.id, { status: 'approved' });
        results.succeeded.push(item);
      } catch (error) {
        results.failed.push({ item, error: error.message });
      }
    }
    
    setBulkActionResults(results);
    setBulkActionLoading(false);
    fetchItems();
    
    if (results.succeeded.length > 0) toast.success(`${results.succeeded.length} item(s) approved`);
    if (results.skipped.length > 0) toast.info(`${results.skipped.length} item(s) skipped`);
    if (results.failed.length > 0) toast.error(`${results.failed.length} item(s) failed`);
  };

  const handleBulkPromote = async (twoFAToken = null) => {
    setBulkActionLoading(true);
    setBulk2FAError('');
    
    // Determine eligible items
    const eligibleItems = selectedItems.filter(item => item.status === 'approved');
    const nonEligibleItems = selectedItems.filter(item => item.status !== 'approved');
    
    // Build skipped list for non-eligible items
    const skippedItems = nonEligibleItems.map(item => ({ 
      item, 
      reason: item.status === 'live' ? 'Already live' : `Not approved (${item.status})` 
    }));
    
    // If we have a 2FA token, process pending items with it
    if (twoFAToken && pendingBulkItems.length > 0) {
      // Start with partial results if we have them (items processed before 2FA was required)
      const results = partialBulkResults 
        ? { ...partialBulkResults }
        : { succeeded: [], skipped: [...skippedItems], failed: [] };
      
      // Process all pending items with the 2FA token
      for (const item of pendingBulkItems) {
        try {
          adminOpsService.setAuthHeaders(getAuthHeaders());
          await adminOpsService.promoteTrainingIntake(item.id, twoFAToken);
          results.succeeded.push(item);
        } catch (error) {
          // Real failure after 2FA verification - this is a genuine error
          results.failed.push({ item, error: error.message });
        }
      }
      
      // Clear pending state and show final results
      setBulkActionResults(results);
      setBulkActionLoading(false);
      setBulk2FARequired(false);
      setBulk2FACode('');
      setPendingBulkItems([]);
      setPartialBulkResults(null);
      fetchItems();
      
      if (results.succeeded.length > 0) toast.success(`${results.succeeded.length} item(s) promoted to Live`);
      if (results.skipped.length > 0) toast.info(`${results.skipped.length} item(s) skipped`);
      if (results.failed.length > 0) toast.error(`${results.failed.length} item(s) failed`);
      return;
    }
    
    // No 2FA token yet - try to process items and detect if 2FA is required
    if (!twoFAToken && eligibleItems.length > 0) {
      const results = { succeeded: [], skipped: [...skippedItems], failed: [] };
      
      // Try to process each eligible item
      for (let i = 0; i < eligibleItems.length; i++) {
        const item = eligibleItems[i];
        try {
          adminOpsService.setAuthHeaders(getAuthHeaders());
          await adminOpsService.promoteTrainingIntake(item.id);
          results.succeeded.push(item);
        } catch (error) {
          if (error.requires2FA) {
            // 2FA is required - pause execution, store state, show 2FA modal
            // Store ALL remaining items (including current one) as pending
            const remainingItems = eligibleItems.slice(i);
            setPendingBulkItems(remainingItems);
            setBulk2FARequired(true);
            setBulkActionLoading(false);
            
            // Store partial results (items that succeeded before 2FA was required)
            // DO NOT set bulkActionResults - this keeps the 2FA modal visible
            setPartialBulkResults(results);
            
            // Exit without setting bulkActionResults - this is the key fix
            return;
          }
          // Other error - mark as failed
          results.failed.push({ item, error: error.message });
        }
      }
      
      // All items processed without 2FA requirement
      setBulkActionResults(results);
      setBulkActionLoading(false);
      fetchItems();
      
      if (results.succeeded.length > 0) toast.success(`${results.succeeded.length} item(s) promoted to Live`);
      if (results.skipped.length > 0) toast.info(`${results.skipped.length} item(s) skipped`);
      if (results.failed.length > 0) toast.error(`${results.failed.length} item(s) failed`);
      return;
    }
    
    // No eligible items
    const results = { succeeded: [], skipped: [...skippedItems], failed: [] };
    setBulkActionResults(results);
    setBulkActionLoading(false);
    
    if (results.skipped.length > 0) toast.info(`${results.skipped.length} item(s) skipped`);
  };

  const handleBulkDelete = async (twoFAToken = null) => {
    setBulkActionLoading(true);
    setBulk2FAError('');
    
    // All selected items are eligible for delete (unless already deleted/archived)
    const eligibleItems = selectedItems.filter(item => item.status !== 'archived');
    const nonEligibleItems = selectedItems.filter(item => item.status === 'archived');
    
    // Build skipped list for non-eligible items
    const skippedItems = nonEligibleItems.map(item => ({ 
      item, 
      reason: 'Already archived' 
    }));
    
    // If we have a 2FA token, process pending items with it
    if (twoFAToken && pendingBulkItems.length > 0) {
      const results = partialBulkResults 
        ? { ...partialBulkResults }
        : { succeeded: [], skipped: [...skippedItems], failed: [] };
      
      for (const item of pendingBulkItems) {
        try {
          adminOpsService.setAuthHeaders(getAuthHeaders());
          await adminOpsService.deleteTrainingIntake(item.id, { 
            reason: 'Bulk delete', 
            force: true,
            twoFAToken 
          });
          results.succeeded.push(item);
        } catch (error) {
          results.failed.push({ item, error: error.message });
        }
      }
      
      setBulkActionResults(results);
      setBulkActionLoading(false);
      setBulk2FARequired(false);
      setBulk2FACode('');
      setPendingBulkItems([]);
      setPartialBulkResults(null);
      fetchItems();
      
      if (results.succeeded.length > 0) toast.success(`${results.succeeded.length} item(s) deleted`);
      if (results.skipped.length > 0) toast.info(`${results.skipped.length} item(s) skipped`);
      if (results.failed.length > 0) toast.error(`${results.failed.length} item(s) failed`);
      return;
    }
    
    // No 2FA token yet - try to process items
    if (!twoFAToken && eligibleItems.length > 0) {
      const results = { succeeded: [], skipped: [...skippedItems], failed: [] };
      
      for (let i = 0; i < eligibleItems.length; i++) {
        const item = eligibleItems[i];
        try {
          adminOpsService.setAuthHeaders(getAuthHeaders());
          await adminOpsService.deleteTrainingIntake(item.id, { 
            reason: 'Bulk delete', 
            force: true 
          });
          results.succeeded.push(item);
        } catch (error) {
          if (error.requires2FA) {
            // 2FA required - pause and show modal
            const remainingItems = eligibleItems.slice(i);
            setPendingBulkItems(remainingItems);
            setBulk2FARequired(true);
            setBulkActionLoading(false);
            setPartialBulkResults(results);
            return;
          }
          results.failed.push({ item, error: error.message });
        }
      }
      
      setBulkActionResults(results);
      setBulkActionLoading(false);
      fetchItems();
      
      if (results.succeeded.length > 0) toast.success(`${results.succeeded.length} item(s) deleted`);
      if (results.skipped.length > 0) toast.info(`${results.skipped.length} item(s) skipped`);
      if (results.failed.length > 0) toast.error(`${results.failed.length} item(s) failed`);
      return;
    }
    
    // No eligible items
    const results = { succeeded: [], skipped: [...skippedItems], failed: [] };
    setBulkActionResults(results);
    setBulkActionLoading(false);
    
    if (results.skipped.length > 0) toast.info(`${results.skipped.length} item(s) skipped`);
  };

  // Handle 2FA verification for bulk action
  const handleBulk2FAVerify = async () => {
    if (!bulk2FACode || bulk2FACode.length < 6) {
      setBulk2FAError('Please enter a valid 6-digit code');
      return;
    }
    
    setBulk2FAVerifying(true);
    setBulk2FAError('');
    
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      // Determine the action type for verification
      const action = bulkAction === 'delete' ? 'intake_delete' : 'intake_promote';
      // For BULK actions, pass null as targetId to get a multi-use token
      // that can be used for all items in the batch
      const data = await adminOpsService.verify2FA(bulk2FACode, action, null);
      
      if (data.success && data.token) {
        toast.success('2FA verified - continuing bulk action');
        // Continue bulk action with the token
        if (bulkAction === 'promote') {
          await handleBulkPromote(data.token);
        } else if (bulkAction === 'delete') {
          await handleBulkDelete(data.token);
        }
      } else {
        setBulk2FAError(data.error || 'Verification failed');
      }
    } catch (err) {
      console.error('Bulk 2FA verification error:', err);
      if (err.message?.includes('Invalid verification code') || err.message?.includes('Invalid code')) {
        setBulk2FAError('Invalid verification code. Please check your authenticator app and try again.');
      } else {
        setBulk2FAError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setBulk2FAVerifying(false);
    }
  };

  // Handle cancellation of bulk 2FA
  const handleBulk2FACancel = () => {
    setBulk2FARequired(false);
    setBulk2FACode('');
    setBulk2FAError('');
    setBulkActionLoading(false);
    
    // Show cancelled state with info about what was/wasn't processed
    const cancelledCount = pendingBulkItems.length;
    const partialSucceeded = partialBulkResults?.succeeded?.length || 0;
    
    setBulkActionResults({
      succeeded: partialBulkResults?.succeeded || [],
      skipped: partialBulkResults?.skipped || [],
      failed: [],
      cancelled: true,
      cancelledCount: cancelledCount
    });
    
    setPendingBulkItems([]);
    setPartialBulkResults(null);
    
    // Refresh if any items were processed before cancellation
    if (partialSucceeded > 0) {
      fetchItems();
    }
  };

  const executeBulkAction = () => {
    if (bulkAction === 'approve') handleBulkApprove();
    else if (bulkAction === 'promote') handleBulkPromote();
    else if (bulkAction === 'delete') handleBulkDelete();
  };

  const closeBulkActionDialog = () => {
    setShowBulkActionDialog(false);
    setBulkAction(null);
    setBulkActionResults(null);
    setBulk2FARequired(false);
    setBulk2FACode('');
    setBulk2FAError('');
    setPendingBulkItems([]);
    setPartialBulkResults(null);
    if (bulkActionResults?.succeeded?.length > 0) handleClearSelection();
  };

  const archivedCount = statusCounts.archived || 0;

  return (
    <div className="space-y-6" data-testid="training-intake-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Upload className="text-amber-400" size={28} />
            Training Intake
          </h1>
          <p className="text-slate-500 mt-1">Upload and manage training materials</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={showArchived ? "default" : "outline"} onClick={() => { setShowArchived(!showArchived); if (!showArchived) setStatusFilter('all'); }} className={showArchived ? "bg-slate-600" : ""}>
            <Archive size={18} className="mr-2" /> {showArchived ? 'Hide' : 'View'} Archived
            {archivedCount > 0 && <span className="ml-2 px-1.5 py-0.5 bg-slate-700 rounded text-xs">{archivedCount}</span>}
          </Button>
          <Button onClick={() => setShowUpload(true)} data-testid="add-material-btn">
            <Plus size={18} className="mr-2" /> Add Material
          </Button>
        </div>
      </div>

      {/* Status tabs */}
      {!showArchived && (
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'all', label: 'All', count: total },
            { value: 'uploaded', label: 'Uploaded', count: statusCounts.uploaded || 0 },
            { value: 'draft', label: 'Draft', count: statusCounts.draft || 0 },
            { value: 'needs_review', label: 'Needs Review', count: statusCounts.needs_review || 0 },
            { value: 'approved', label: 'Approved', count: statusCounts.approved || 0 },
            { value: 'live', label: 'Live', count: statusCounts.live || 0 },
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                statusFilter === tab.value ? 'bg-amber-600 text-white' : 'bg-stone-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded text-xs ${statusFilter === tab.value ? 'bg-white/20' : 'bg-slate-700'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search materials..." className="pl-10 bg-stone-900 border-stone-800 text-white" />
        </div>
        <Select value={contentTypeFilter} onValueChange={setContentTypeFilter}>
          <SelectTrigger className="w-44 bg-stone-900 border-stone-800 text-white"><SelectValue placeholder="Content Type" /></SelectTrigger>
          <SelectContent className="bg-stone-800 border-slate-700">
            <SelectItem value="all">All Types</SelectItem>
            {CONTENT_TYPES.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={destinationFilter} onValueChange={setDestinationFilter}>
          <SelectTrigger className="w-44 bg-stone-900 border-stone-800 text-white"><SelectValue placeholder="Destination" /></SelectTrigger>
          <SelectContent className="bg-stone-800 border-slate-700">
            <SelectItem value="all">All Destinations</SelectItem>
            {DESTINATIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={fetchItems}><RefreshCw size={18} /></Button>
      </div>

      {/* Bulk Selection Bar */}
      {filteredItems.length > 0 && (
        <div className="flex items-center justify-between bg-stone-900 border border-stone-800 rounded-lg px-4 py-3" data-testid="bulk-selection-bar">
          <div className="flex items-center gap-4">
            {/* Select All Checkbox with proper indeterminate state */}
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected}
                onCheckedChange={handleSelectAll}
                className={`h-5 w-5 border-2 ${allVisibleSelected ? 'border-amber-500 bg-amber-500' : someVisibleSelected ? 'border-amber-400' : 'border-slate-600'}`}
                data-testid="select-all-checkbox"
              />
              <span className="text-sm text-slate-400">
                {noneSelected 
                  ? 'Select All' 
                  : allVisibleSelected 
                    ? `All ${filteredItems.length} Selected`
                    : `${visibleSelectedCount} Selected`
                }
              </span>
            </div>
            
            {/* Clear selection button */}
            {visibleSelectedCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleClearSelection}
                className="text-slate-500 hover:text-white"
              >
                <X size={14} className="mr-1" />
                Clear
              </Button>
            )}
          </div>
          
          {/* Bulk Actions Dropdown */}
          {visibleSelectedCount > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-amber-600 hover:bg-amber-700" data-testid="bulk-actions-btn">
                  <Settings size={16} className="mr-2" />
                  Actions
                  <ChevronDown size={14} className="ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-stone-800 border-slate-700" align="end">
                <DropdownMenuItem 
                  className="text-emerald-400 hover:bg-emerald-500/10 cursor-pointer"
                  onClick={() => openBulkActionDialog('approve')}
                  data-testid="bulk-approve-btn"
                >
                  <CheckCircle size={16} className="mr-2" />
                  Approve Selected
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="text-amber-400 hover:bg-amber-500/10 cursor-pointer"
                  onClick={() => openBulkActionDialog('promote')}
                  data-testid="bulk-promote-btn"
                >
                  <Send size={16} className="mr-2" />
                  Promote to Live
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                  onClick={() => openBulkActionDialog('delete')}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete Selected
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      {/* Items grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-64 bg-stone-800" />)}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-16 bg-stone-900 rounded-xl border border-stone-800">
          <Upload className="mx-auto text-slate-600 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white mb-2">No Materials Found</h3>
          <p className="text-slate-500 mb-4">
            {searchQuery || statusFilter !== 'all' || contentTypeFilter !== 'all' || destinationFilter !== 'all'
              ? 'No materials match your filters.'
              : 'Start by adding your first training material.'}
          </p>
          <Button onClick={() => setShowUpload(true)}><Plus size={18} className="mr-2" /> Add Material</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map(item => (
            <IntakeItemCard
              key={item.id}
              item={item}
              isSelected={selectedIds.has(item.id)}
              onToggleSelect={handleToggleSelect}
              onView={(item) => { setViewingItem(item); setViewMode('view'); }}
              onEdit={(item) => { setViewingItem(item); setViewMode('edit'); }}
              onDuplicate={handleDuplicate}
              onPromote={handlePromote}
              onArchive={handleArchive}
              onRestore={handleRestore}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      )}

      {/* Dialogs */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <UploadDialog open={showUpload} onClose={() => setShowUpload(false)} onSubmit={handleCreate} />
      </Dialog>

      <Dialog open={!!viewingItem} onOpenChange={(open) => { if (!open) setViewingItem(null); }}>
        {viewingItem && (
          <ItemDetailDialog
            item={viewingItem}
            mode={viewMode}
            onClose={() => setViewingItem(null)}
            onUpdate={handleUpdate}
            onPromote={handlePromote}
            onDuplicate={handleDuplicate}
            onRestore={handleRestore}
          />
        )}
      </Dialog>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={showBulkActionDialog} onOpenChange={(open) => { if (!open && !bulk2FAVerifying) closeBulkActionDialog(); }}>
        <DialogContent className="max-w-lg bg-stone-900 border-stone-800" data-testid="bulk-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              {bulk2FARequired ? (
                <Shield className="text-amber-400" size={20} />
              ) : bulkAction === 'approve' ? (
                <CheckCircle className="text-emerald-400" size={20} />
              ) : bulkAction === 'delete' ? (
                <Trash2 className="text-red-400" size={20} />
              ) : (
                <Send className="text-amber-400" size={20} />
              )}
              {bulk2FARequired 
                ? 'Two-Factor Verification Required'
                : bulkAction === 'approve' 
                  ? 'Bulk Approve' 
                  : bulkAction === 'delete'
                    ? 'Bulk Delete'
                    : 'Bulk Promote to Live'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {bulk2FARequired 
                ? 'This action requires 2FA verification to continue.'
                : bulkActionResults 
                  ? (bulkActionResults.cancelled ? 'Action was cancelled.' : 'Action completed. See results below.')
                  : bulkAction === 'delete'
                    ? `${selectedItems.length} item(s) selected for deletion. This action cannot be undone.`
                    : `${selectedItems.length} item(s) selected for ${bulkAction === 'approve' ? 'approval' : 'promotion'}.`
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* 2FA Verification Step */}
          {bulk2FARequired && !bulkActionResults ? (
            <div className="space-y-4 py-4">
              {/* 2FA Info Banner */}
              <div className={`${bulkAction === 'delete' ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/30'} border rounded-lg p-4`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className={bulkAction === 'delete' ? 'text-red-400' : 'text-amber-400'} size={18} />
                  <div>
                    <p className="text-sm text-white font-medium">
                      {bulkAction === 'delete' 
                        ? 'Bulk Delete requires verification'
                        : 'Bulk Promote to Live requires verification'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {pendingBulkItems.length} item(s) pending {bulkAction === 'delete' ? 'deletion' : 'promotion'}
                    </p>
                    <p className={`text-xs mt-2 ${bulkAction === 'delete' ? 'text-red-400' : 'text-amber-400'}`}>
                      Enter your authentication code to continue with the bulk action.
                    </p>
                  </div>
                </div>
              </div>

              {/* OTP Input */}
              <div className="space-y-2">
                <Label className="text-slate-300">Verification Code</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter 6-digit code"
                    value={bulk2FACode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setBulk2FACode(value);
                      setBulk2FAError('');
                    }}
                    className="pl-10 bg-stone-800 border-slate-700 text-center text-lg tracking-widest font-mono"
                    autoFocus
                    maxLength={6}
                    data-testid="bulk-2fa-code-input"
                    disabled={bulk2FAVerifying}
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Enter the code from your authenticator app
                </p>
              </div>

              {/* Error Message */}
              {bulk2FAError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle className="text-red-400 shrink-0" size={16} />
                  <p className="text-sm text-red-400">{bulk2FAError}</p>
                </div>
              )}

              {/* Pending items preview */}
              <div className="bg-stone-800/50 rounded-lg p-4 max-h-40 overflow-y-auto">
                <p className="text-xs font-medium text-slate-400 mb-2">Items awaiting verification:</p>
                <div className="space-y-1">
                  {pendingBulkItems.slice(0, 5).map(item => (
                    <div key={item.id} className="text-xs text-slate-300">
                      • {item.title?.slice(0, 45)}{item.title?.length > 45 ? '...' : ''}
                    </div>
                  ))}
                  {pendingBulkItems.length > 5 && (
                    <p className="text-xs text-slate-500">... and {pendingBulkItems.length - 5} more</p>
                  )}
                </div>
              </div>
            </div>
          ) : !bulkActionResults ? (
            <div className="space-y-4 py-4">
              {/* Delete warning */}
              {bulkAction === 'delete' && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-sm text-white font-medium">
                        Warning: This action cannot be undone
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Selected items will be permanently removed from Training Intake.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Selected items preview */}
              <div className="bg-stone-800/50 rounded-lg p-4 max-h-60 overflow-y-auto">
                <p className="text-sm font-medium text-slate-300 mb-3">Selected Items:</p>
                <div className="space-y-2">
                  {selectedItems.slice(0, 10).map(item => {
                    const canApprove = ['draft', 'needs_review', 'uploaded'].includes(item.status);
                    const canPromote = item.status === 'approved';
                    const canDelete = item.status !== 'archived';
                    const isEligible = bulkAction === 'approve' ? canApprove : bulkAction === 'delete' ? canDelete : canPromote;
                    
                    return (
                      <div key={item.id} className="flex items-center justify-between text-sm">
                        <span className={isEligible ? 'text-white' : 'text-slate-500 line-through'}>
                          {item.title?.slice(0, 40)}{item.title?.length > 40 ? '...' : ''}
                        </span>
                        {!isEligible && (
                          <span className="text-xs text-amber-400">
                            {bulkAction === 'delete' 
                              ? 'Already archived'
                              : item.status === 'live' ? 'Already live' : `Status: ${item.status}`}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {selectedItems.length > 10 && (
                    <p className="text-xs text-slate-500">... and {selectedItems.length - 10} more</p>
                  )}
                </div>
              </div>
              
              {/* Eligibility summary */}
              <div className="bg-stone-800/50 rounded-lg p-4">
                <p className="text-sm text-slate-300">
                  {bulkAction === 'approve' ? (
                    <>
                      <span className="text-emerald-400 font-medium">
                        {selectedItems.filter(i => ['draft', 'needs_review', 'uploaded'].includes(i.status)).length}
                      </span>
                      {' eligible for approval, '}
                      <span className="text-amber-400 font-medium">
                        {selectedItems.filter(i => !['draft', 'needs_review', 'uploaded'].includes(i.status)).length}
                      </span>
                      {' will be skipped'}
                    </>
                  ) : bulkAction === 'delete' ? (
                    <>
                      <span className="text-red-400 font-medium">
                        {selectedItems.filter(i => i.status !== 'archived').length}
                      </span>
                      {' eligible for deletion, '}
                      <span className="text-amber-400 font-medium">
                        {selectedItems.filter(i => i.status === 'archived').length}
                      </span>
                      {' will be skipped'}
                    </>
                  ) : (
                    <>
                      <span className="text-amber-400 font-medium">
                        {selectedItems.filter(i => i.status === 'approved').length}
                      </span>
                      {' eligible for promotion, '}
                      <span className="text-amber-400 font-medium">
                        {selectedItems.filter(i => i.status !== 'approved').length}
                      </span>
                      {' will be skipped'}
                    </>
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Cancelled state */}
              {bulkActionResults.cancelled && (
                <div className="bg-stone-800/50 border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <XCircle className="text-slate-400" size={24} />
                    <div>
                      <p className="text-sm font-medium text-white">Bulk action cancelled</p>
                      <p className="text-xs text-slate-400 mt-1">
                        2FA verification was not completed. {bulkActionResults.cancelledCount} item(s) were not processed.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Results summary */}
              {!bulkActionResults.cancelled && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{bulkActionResults.succeeded.length}</p>
                    <p className="text-xs text-slate-400">Succeeded</p>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-amber-400">{bulkActionResults.skipped.length}</p>
                    <p className="text-xs text-slate-400">Skipped</p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-red-400">{bulkActionResults.failed.length}</p>
                    <p className="text-xs text-slate-400">Failed</p>
                  </div>
                </div>
              )}
              
              {/* Detailed results */}
              {!bulkActionResults.cancelled && bulkActionResults.skipped.length > 0 && (
                <div className="bg-stone-800/50 rounded-lg p-4">
                  <p className="text-sm font-medium text-amber-400 mb-2">Skipped Items:</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {bulkActionResults.skipped.map(({ item, reason }) => (
                      <div key={item.id} className="text-xs text-slate-400 flex justify-between">
                        <span>{item.title?.slice(0, 30)}...</span>
                        <span className="text-amber-300">{reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {!bulkActionResults.cancelled && bulkActionResults.failed.length > 0 && (
                <div className="bg-red-500/10 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-400 mb-2">Failed Items:</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {bulkActionResults.failed.map(({ item, error }) => (
                      <div key={item.id} className="text-xs text-slate-400 flex justify-between">
                        <span>{item.title?.slice(0, 30)}...</span>
                        <span className="text-red-300">{error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-row justify-end gap-2">
            {bulk2FARequired && !bulkActionResults ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleBulk2FACancel} 
                  disabled={bulk2FAVerifying}
                  data-testid="bulk-2fa-cancel-btn"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleBulk2FAVerify}
                  disabled={bulk2FAVerifying || bulk2FACode.length < 6}
                  className={bulkAction === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
                  data-testid="bulk-2fa-verify-btn"
                >
                  {bulk2FAVerifying ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Shield size={14} className="mr-2" />
                      Verify & Continue
                    </>
                  )}
                </Button>
              </>
            ) : !bulkActionResults ? (
              <>
                <Button variant="outline" onClick={closeBulkActionDialog} disabled={bulkActionLoading}>
                  Cancel
                </Button>
                <Button 
                  onClick={executeBulkAction}
                  disabled={bulkActionLoading}
                  className={bulkAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : bulkAction === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
                  data-testid="confirm-bulk-action-btn"
                >
                  {bulkActionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {bulkAction === 'approve' ? (
                        <>
                          <CheckCircle size={16} className="mr-2" />
                          Approve {selectedItems.filter(i => ['draft', 'needs_review', 'uploaded'].includes(i.status)).length} Items
                        </>
                      ) : bulkAction === 'delete' ? (
                        <>
                          <Trash2 size={16} className="mr-2" />
                          Delete {selectedItems.filter(i => i.status !== 'archived').length} Items
                        </>
                      ) : (
                        <>
                          <Send size={16} className="mr-2" />
                          Promote {selectedItems.filter(i => i.status === 'approved').length} Items
                        </>
                      )}
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={closeBulkActionDialog} data-testid="bulk-action-close-btn">
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* 2FA Modal for Promote action */}
      <TwoFactorModal
        open={show2FAModal}
        onClose={() => { setShow2FAModal(false); setPending2FAItem(null); }}
        onVerified={handle2FAVerified}
        action="intake_promote"
        actionLabel="Promote to Live"
        targetId={pending2FAItem?.id}
        targetLabel={pending2FAItem?.title}
        authHeaders={getAuthHeaders()}
      />
      
      {/* 2FA Modal for Delete action */}
      <TwoFactorModal
        open={deletePending2FA}
        onClose={() => setDeletePending2FA(false)}
        onVerified={handleDelete2FAVerified}
        action="intake_delete"
        actionLabel="Delete Item"
        targetId={deleteTarget?.id}
        targetLabel={deleteTarget?.title}
        authHeaders={getAuthHeaders()}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { if (!open) { setShowDeleteDialog(false); setDeleteTarget(null); setDeleteInfo(null); } }}>
        <DialogContent className="max-w-lg bg-stone-900 border-stone-800" data-testid="delete-intake-dialog">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${deleteInfo?.risk_level === 'high' ? 'text-red-400' : deleteInfo?.risk_level === 'medium' ? 'text-amber-400' : 'text-white'}`}>
              {deleteInfo?.risk_level === 'high' ? (
                <AlertTriangle className="text-red-400" />
              ) : deleteInfo?.risk_level === 'medium' ? (
                <Shield className="text-amber-400" />
              ) : (
                <Trash2 className="text-slate-400" />
              )}
              Delete Training Intake Item
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {deleteInfo?.risk_level === 'low' 
                ? 'This item can be safely deleted.'
                : 'This is a protected action that requires additional verification.'}
            </DialogDescription>
          </DialogHeader>
          
          {deleteTarget && deleteInfo && (
            <div className="space-y-4 py-4">
              {/* Item Info */}
              <div className="bg-stone-800/50 rounded-lg p-4">
                <h4 className="font-medium text-white mb-2">{deleteTarget.title}</h4>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full border ${STATUS_CONFIG[deleteTarget.status]?.color || 'border-slate-600 text-slate-400'}`}>
                    {STATUS_CONFIG[deleteTarget.status]?.label || deleteTarget.status}
                  </span>
                  {deleteInfo.is_test_item && (
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">TEST ITEM</span>
                  )}
                  {deleteInfo.has_linked_data && (
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded flex items-center gap-1">
                      <Link size={10} /> Has Linked Data
                    </span>
                  )}
                </div>
              </div>
              
              {/* Warnings */}
              {deleteInfo.warnings?.length > 0 && (
                <div className={`rounded-lg p-4 ${deleteInfo.risk_level === 'high' ? 'bg-red-500/10 border border-red-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={18} className={deleteInfo.risk_level === 'high' ? 'text-red-400' : 'text-amber-400'} />
                    <div className="space-y-1">
                      {deleteInfo.warnings.map((warning, i) => (
                        <p key={i} className={`text-sm ${deleteInfo.risk_level === 'high' ? 'text-red-300' : 'text-amber-300'}`}>
                          {warning}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Linked Entries */}
              {deleteInfo.linked_entries?.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Linked Destination Entries</Label>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="delete-linked"
                        checked={deleteLinked}
                        onCheckedChange={setDeleteLinked}
                      />
                      <label htmlFor="delete-linked" className="text-xs text-slate-400 cursor-pointer">
                        Also archive linked entries
                      </label>
                    </div>
                  </div>
                  <div className="bg-stone-800/30 rounded-lg p-3 space-y-2">
                    {deleteInfo.linked_entries.map((entry, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                        <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded">
                          {entry.destination.replace('_', ' ')}
                        </span>
                        <span>{entry.name || entry.id}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">
                    {deleteLinked 
                      ? 'Linked entries will be archived when this item is deleted.'
                      : 'Linked entries will be marked as orphaned but not deleted.'}
                  </p>
                </div>
              )}
              
              {/* Reason (for protected deletions) */}
              {deleteInfo.requires_reason && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Reason for deletion <span className="text-red-400">*</span></Label>
                  <Textarea
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Why is this item being deleted?"
                    className="bg-stone-800 border-slate-700 text-white resize-none"
                    rows={2}
                    data-testid="delete-reason-input"
                  />
                  <p className="text-xs text-slate-500">Minimum 5 characters (for audit log)</p>
                </div>
              )}
              
              {/* 2FA Code (for protected deletions) */}
              {deleteInfo.requires_2fa && (
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Shield size={14} className="text-amber-400" />
                    2FA Verification Required
                  </Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter 6-digit code"
                    value={delete2FACode}
                    onChange={(e) => setDelete2FACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="bg-stone-800 border-slate-700 text-white text-center tracking-widest font-mono"
                    maxLength={6}
                    data-testid="delete-2fa-input"
                  />
                  <p className="text-xs text-slate-500">Enter code from your authenticator app</p>
                </div>
              )}
              
              {/* Risk level indicator */}
              <div className="flex items-center gap-2 pt-2 border-t border-stone-800">
                <span className={`px-2 py-1 text-xs rounded ${
                  deleteInfo.risk_level === 'high' ? 'bg-red-500/20 text-red-400' :
                  deleteInfo.risk_level === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-slate-700 text-slate-400'
                }`}>
                  {deleteInfo.risk_level?.toUpperCase()} RISK
                </span>
                <span className="text-xs text-slate-500">
                  {deleteInfo.requires_2fa ? 'Requires 2FA' : ''} 
                  {deleteInfo.requires_2fa && deleteInfo.requires_reason ? ' + ' : ''}
                  {deleteInfo.requires_reason ? 'Requires reason' : ''}
                  {!deleteInfo.requires_2fa && !deleteInfo.requires_reason ? 'Confirmation only' : ''}
                </span>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowDeleteDialog(false); setDeleteTarget(null); setDeleteInfo(null); }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleDeleteConfirm()}
              disabled={deleteLoading || (deleteInfo?.requires_reason && (!deleteReason || deleteReason.length < 5)) || (deleteInfo?.requires_2fa && delete2FACode.length !== 6)}
              data-testid="confirm-delete-btn"
            >
              {deleteLoading ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <Trash2 size={14} className="mr-2" />
              )}
              {deleteInfo?.risk_level === 'low' ? 'Delete' : 'Delete (Protected)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingIntakePage;
